import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { SongLearnCanvasBackdrop } from "../components/SongLearnCanvasBackdrop";
import { WorkbenchLayout } from "../components/layout/WorkbenchLayout";
import { PracticeSurface } from "../components/practice/PracticeSurface";
import {
  buildScalePattern,
  buildScaleSequence,
  buildScaleHandFriendlySequence,
  buildPedagogicalOctaveSequence,
  getNoteAtPosition,
  getFretPositions,
  getNeckGeometry,
  noteToSemitones,
  NECK_CONFIG,
  type NoteName,
  type NeckPosition,
} from "../lib/guitarEngine";
import { JUMP_GRAPH, STRING_NAMES } from "../lib/noteNavigator";
import { getInstrumentConfig } from "../lib/instrumentEngine";
import { useInstrument } from "../contexts/InstrumentContext";
import { usePracticeMode } from "../contexts/PracticeModeContext";
import { useExploreSandbox } from "../hooks/useExploreSandbox";
import { useWorkbench } from "../components/layout/WorkbenchLayout";
import { usePitchDetector } from "../hooks/usePitchDetector";
import { useGuitarSynth } from "../hooks/useGuitarSynth";
import { useStudentFavorites } from "../hooks/useStudentFavorites";
import { PracticeIntroModal } from "../components/PracticeIntroModal";
import { TaskCreatorButton } from "../components/TaskCreatorButton";
import { Bookmark } from "lucide-react";
import { serializeScaleState } from "../lib/taskEngine";
import { SATURATED_DEGREE_HEX } from "../lib/scaleModuleTheme";
import {
  generateScaleFingerings,
  fingeringToNeckPositions,
  buildBoxConnectionSequence,
  type FretboardPattern,
} from "../lib/fingeringEngine";
import {
  generateCagedPatterns,
  generateExtendedCagedPatterns,
  buildCagedMixedSequence,
  findPatternConnections,
  getCagedNoteContext,
  toFretboardPattern,
  getTransitionGuide,
  type CagedPattern,
  type TransitionGuide,
  CAGED_DEFINITIONS,
} from "../lib/cagedEngine";
// Selectores reutilizados del ChordBuilder (circulares con pickers desplegables)
import type { NeckConnection } from "../components/practice/PracticeSurface";
import { HelpTooltip } from "../components/ui/HelpTooltip";
import { PracticeStaff } from "../components/practice/PracticeStaff";
import { PRACTICE_STAFF_MIN_SCALE_KEYBOARD } from "../lib/practiceStaffLayout";
import { useSpaciousWorkbench } from "../lib/useSpaciousWorkbench";
import { ScalePlaybackControls } from "../components/scales/ScalePlaybackControls";
import { ScaleHorizontalDock } from "../components/scales/ScaleHorizontalDock";
import { ScalePianoInspector } from "../components/scales/ScalePianoInspector";
import { ExploreImmersiveCanvas } from "../components/scales/ExploreImmersiveCanvas";
import { ExploreSandboxTransport } from "../components/scales/ExploreSandboxTransport";
import {
  selectCanonical3NpsFingerings,
  fingeringNoteKeys,
  fingeringToPlaybackSequence,
  type ExploreNpsSelection,
  type ExplorePlaybackRange,
} from "../lib/explore3nps";
import { findNeckPositionForMidi, midiToNoteAtPosition } from "../lib/practiceSurface";
import {
  buildPianoHighlights,
  buildPianoLearnKeyboardHighlights,
  buildPianoLevel1PlaybackMidis,
  buildPianoStepStaffEvents,
  PIANO_LEVEL1_STEPS,
  pianoStepDescription,
  type PianoFocusStep,
  type PianoIntervalDirection,
} from "../lib/pianoPedagogy";
import {
  getPianoLearnIntro,
  getPianoLearnTargetMidis,
  getPianoIntervalTransitionIntro,
  getPianoStepCompleteContent,
} from "../lib/pianoLearnIntros";
import type { PracticeNoteEvent } from "../lib/practiceNoteModel";

const NOTES: NoteName[] = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

const SCALE_TYPES = [
  { value: "major", label: "Mayor" },
  { value: "minor", label: "Menor Natural" },
  { value: "minor_harmonic", label: "Menor Armónica" },
  { value: "minor_melodic", label: "Menor Melódica" },
  { value: "pentatonic_major", label: "Pent. Mayor" },
  { value: "pentatonic_minor", label: "Pent. Menor" },
  { value: "blues", label: "Blues" },
];

const PATTERNS = [
  { value: "linear", label: "Lineal" },
  { value: "thirds", label: "Saltos 3ra" },
  { value: "fourths", label: "Saltos 4ta" },
  { value: "fifths", label: "Saltos 5ta" },
];

const SCALE_INFO: Record<string, string> = {
  major: "Mayor: la base de la armonía occidental. 7 notas con sonido alegre/estable.",
  minor: "Menor natural: carácter melancólico. Su mayor relativa está a +3 semitonos.",
  minor_harmonic: "Menor armónica: eleva la 7ª para crear V7 dominante. Sonido exótico.",
  minor_melodic: "Menor melódica: eleva 6ª y 7ª ascendente, natural descendente.",
  pentatonic_major: "Pentatónica mayor: elimina semitonos problemáticos. Muy usada.",
  pentatonic_minor: "Pentatónica menor: escala del blues y rock. 5 notas poderosas.",
  blues: "Blues: añade 'nota azul' (#4/b5) para tensión cromática.",
};

// Colores para múltiples cajas CAGED
const BOX_COLORS = [
  "#FF4D4D", "#2DD4BF", "#22C55E", "#A07840", "#8B5A7C", "#4A6B8A", "#C44B4B", "#2C8F5E",
];

// ─── Pools de escalas por nivel (bloqueadas) ───
const LEVEL_NOTE_POOLS: Record<number, NoteName[]> = {
  1: ["C", "G", "D", "F", "Bb"],
  2: ["C", "G", "D", "F", "Bb", "A", "E", "Eb", "Ab"],
  3: ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"],
};

// ─── Tipo de misión de práctica ───
interface PracticeMission {
  id: number;
  root: NoteName;
  scaleType: string;
  step: 1 | 2 | 3 | 4;
  title: string;
  instruction: string;
}

export const ScaleExplorerPage: React.FC = () => {
  // ─── Estado principal ───
  const [rootNote, setRootNote] = useState<NoteName>("C");
  const [scaleType, setScaleType] = useState("major");
  const [octaves, setOctaves] = useState(1);
  const [direction, setDirection] = useState<"asc" | "desc" | "both">("asc");
  const [pattern, setPattern] = useState("linear");
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSeqIndex, setCurrentSeqIndex] = useState(0);
  const [bpm, setBpm] = useState(60);
  const [activeNote, setActiveNote] = useState<{ string: number; fret: number } | null>(null);
  const [practiceFeedback, setPracticeFeedback] = useState<"success" | "fail" | "silence" | null>(null);
  const [practiceScore, setPracticeScore] = useState(0);
  const [practiceAttempts, setPracticeAttempts] = useState(0);
  const [practiceStartTime, setPracticeStartTime] = useState<number | null>(null);
  const [practiceElapsed, setPracticeElapsed] = useState(0);
  const [practicePhase, setPracticePhase] = useState<"countdown" | "listen" | "success" | "fail" | "silence">("listen");
  const [showPracticeIntro, setShowPracticeIntro] = useState(false);

  // ─── NUEVO: Modo descubrimiento de tónicas (aprendizaje activo Nivel 1) ───
  const [showLearnIntro, setShowLearnIntro] = useState(false);
  const [showPianoLearnIntro, setShowPianoLearnIntro] = useState(false);
  const [pianoLearnActive, setPianoLearnActive] = useState(false);
  const [pianoLearnIntervalDir, setPianoLearnIntervalDir] = useState<PianoIntervalDirection>("asc");
  const [showPianoIntervalTransition, setShowPianoIntervalTransition] = useState(false);
  const [pianoLearnFoundMidis, setPianoLearnFoundMidis] = useState<Set<number>>(new Set());
  const [tonicDiscoveryMode, setTonicDiscoveryMode] = useState(false);
  const [discoveredTonics, setDiscoveredTonics] = useState<Set<string>>(new Set());
  const [learnFeedback, setLearnFeedback] = useState<"success" | "fail" | null>(null);
  const [learnPhase, setLearnPhase] = useState<"discovering" | "completed">("discovering");

  // ─── NUEVO: Flujo secuencial de misiones Nivel 1 ───
  const [currentMissionStep, setCurrentMissionStep] = useState<0 | 1 | 2 | 3>(0);
  const [showMissionComplete, setShowMissionComplete] = useState(false);
  const [missionCompleteProps, setMissionCompleteProps] = useState<{
    title: string;
    message: string;
    nextLabel: string;
    nextStep: 0 | 1 | 2 | 3;
    steps?: string[];
    footerTip?: string;
  }>({
    title: "",
    message: "",
    nextLabel: "Siguiente misión",
    nextStep: 0,
  });

  // Modo triadas ordenadas (misión 1: R → 3ra → 5ta)
  const [showTriadOrderedIntro, setShowTriadOrderedIntro] = useState(false);
  const [triadOrderedMode, setTriadOrderedMode] = useState(false);
  const [orderedSelection, setOrderedSelection] = useState<NeckPosition[]>([]);
  const [orderedTriadsDone, setOrderedTriadsDone] = useState<Set<string>>(new Set());
  const orderedTriadTargetCount = 5;

  // Modo triadas libres (misión 2: agrupar de a 3 sin orden)
  const [showTriadFreeIntro, setShowTriadFreeIntro] = useState(false);
  const [triadFreeMissionMode, setTriadFreeMissionMode] = useState(false);
  const [freeTriadsDone, setFreeTriadsDone] = useState<Set<string>>(new Set());
  const freeTriadTargetCount = 5;

  // Modo pentatónico (paso 3: 4 misiones)
  const [showPentatonicIntro, setShowPentatonicIntro] = useState(false);
  const [pentatonicMissionMode, setPentatonicMissionMode] = useState(false);
  const [pentatonicSubStep, setPentatonicSubStep] = useState<0 | 1 | 2 | 3>(0);
  const [pentatonicPathSelection, setPentatonicPathSelection] = useState<NeckPosition[]>([]);
  const [pentatonicPathsDone, setPentatonicPathsDone] = useState<Set<string>>(new Set());
  const [pentatonicSavedPaths, setPentatonicSavedPaths] = useState<NeckPosition[][]>([]);
  const pentatonicSavedPathsRef = useRef<NeckPosition[][]>([]);
  useEffect(() => { pentatonicSavedPathsRef.current = pentatonicSavedPaths; }, [pentatonicSavedPaths]);
  const [pentatonicPlayTarget, setPentatonicPlayTarget] = useState<NeckPosition[]>([]);
  const [pentatonicPlayIndex, setPentatonicPlayIndex] = useState(0);
  const [showPentatonicPathComplete, setShowPentatonicPathComplete] = useState(false);
  const [pentatonicPathCompleteProps, setPentatonicPathCompleteProps] = useState({
    title: "",
    message: "",
    nextLabel: "Siguiente camino",
    isFinal: false,
  });

  // Modo octava / escala completa (paso 4: 4 misiones)
  const [showOctaveIntro, setShowOctaveIntro] = useState(false);
  const [octaveMissionMode, setOctaveMissionMode] = useState(false);
  const [octaveSubStep, setOctaveSubStep] = useState<0 | 1 | 2 | 3>(0);
  const [octavePathSelection, setOctavePathSelection] = useState<NeckPosition[]>([]);
  const [octavePathsDone, setOctavePathsDone] = useState<Set<string>>(new Set());
  const [octaveSavedPaths, setOctaveSavedPaths] = useState<NeckPosition[][]>([]);
  const octaveSavedPathsRef = useRef<NeckPosition[][]>([]);
  useEffect(() => { octaveSavedPathsRef.current = octaveSavedPaths; }, [octaveSavedPaths]);
  const [octavePlayTarget, setOctavePlayTarget] = useState<NeckPosition[]>([]);
  const [octavePlayIndex, setOctavePlayIndex] = useState(0);
  const [showOctavePathComplete, setShowOctavePathComplete] = useState(false);
  const [octavePathCompleteProps, setOctavePathCompleteProps] = useState({
    title: "",
    message: "",
    nextLabel: "Siguiente camino",
    isFinal: false,
  });

  // ─── NUEVO: Cajas unificadas (CAGED + 3NPS) ───
  const [selectedPatterns, setSelectedPatterns] = useState<FretboardPattern[]>([]);

  // ─── NUEVO: Posición de inicio personalizada ───
  const [startFromRoot, setStartFromRoot] = useState(true);
  const [startPosition, setStartPosition] = useState<{ string: number; fret: number } | null>(null);
  const [isPickingStart, setIsPickingStart] = useState(false);

  // ─── NUEVO: Modo de ruta mnemotécnica ───
  type RouteMode = "off" | "tonic" | "transition" | "recorrido";
  const [routeMode, setRouteMode] = useState<RouteMode>("off");

  // ─── Camino personalizado (Ctrl+Click) ───
  const [customSequence, setCustomSequence] = useState<NeckPosition[]>([]);
  const [isBuildingCustomPath, setIsBuildingCustomPath] = useState(false);

  const [inspectedFret, setInspectedFret] = useState<{ string: number; fret: number } | null>(null);

  const [stringOrder, setStringOrder] = useState<"tab" | "physical">("physical");
  const [leftHanded, setLeftHanded] = useState(false);
  const [focusLevel, setFocusLevel] = useState<1 | 2 | 3 | 4 | 5>(1);
  /** Evita que el reset al cambiar paso cierre el modal de Aprender recién abierto. */
  const pianoKeepIntroRef = useRef(false);
  const [pedagogicalLevel, setPedagogicalLevel] = useState<1 | 2 | 3>(1);

  // ─── Agrupación de triadas ───
  const [triadGroups, setTriadGroups] = useState<Array<{ positions: NeckPosition[]; number: number }>>([]);
  const [currentTriadSelection, setCurrentTriadSelection] = useState<NeckPosition[]>([]);

  const { instrument, surface } = useInstrument();
  const { useBottomDock } = useSpaciousWorkbench();
  const scaleLabel = SCALE_TYPES.find((s) => s.value === scaleType)?.label ?? scaleType;
  const routeLocked =
    tonicDiscoveryMode ||
    triadOrderedMode ||
    triadFreeMissionMode ||
    pentatonicMissionMode ||
    octaveMissionMode;

  const playbackControls = (
    <ScalePlaybackControls
      rootNote={rootNote}
      scaleType={scaleType}
      scaleLabel={scaleLabel}
      routeMode={routeMode}
      setRouteMode={setRouteMode}
      octaves={octaves}
      setOctaves={setOctaves}
      direction={direction}
      setDirection={setDirection}
      pattern={pattern}
      bpm={bpm}
      setBpm={setBpm}
      routeLocked={routeLocked}
    />
  );
  const { mode, setMode } = usePracticeMode();
  const isSandbox = useExploreSandbox();
  const [sandboxNpsSelection, setSandboxNpsSelection] = useState<ExploreNpsSelection>("full");
  const [sandboxPlaybackRange, setSandboxPlaybackRange] = useState<ExplorePlaybackRange>("octave");
  const [staffHoverNote, setStaffHoverNote] = useState<{ string: number; fret: number } | null>(null);
  const { setLeftCollapsed } = useWorkbench();

  useEffect(() => {
    if (!isSandbox) return;
    setPedagogicalLevel(3);
    setFocusLevel(surface === "keyboard" ? 5 : 4);
    if (mode === "practice") setMode("learn");
  }, [isSandbox, surface, mode, setMode]);
  const { playFret, playNote } = useGuitarSynth();
  const { toggleFavorite, isFavorite } = useStudentFavorites();
  const [focusedDegree, setFocusedDegree] = useState<string | null>(null);

  useEffect(() => {
    setFocusedDegree(null);
  }, [rootNote, scaleType]);

  useEffect(() => {
    if (surface !== "keyboard" && focusLevel > 4) setFocusLevel(4);
  }, [surface, focusLevel]);

  useEffect(() => {
    setPianoLearnActive(false);
    setPianoLearnIntervalDir("asc");
    setShowPianoIntervalTransition(false);
    setPianoLearnFoundMidis(new Set());
    setLearnPhase("discovering");
    if (!pianoKeepIntroRef.current) {
      setShowPianoLearnIntro(false);
    }
    pianoKeepIntroRef.current = false;
  }, [focusLevel, rootNote, scaleType, pedagogicalLevel, surface]);
  const instrumentConfig = getInstrumentConfig(instrument);
  const stringCount = instrumentConfig.strings.length;
  const frets = instrumentConfig.fretCount;

  // ─── Configuración de niveles pedagógicos ───
  const LEVEL_CONFIG = {
    1: {
      tonicMaxFret: 5,
      triadMaxFret: 5,
      pentatonicMaxFret: 5,
      octaveMaxFret: 5,
      desc: "Primeros 5 trastes",
      stepDesc: [
        "Tónica de los primeros 5 trastes",
        "Triadas hasta el traste 5",
        "Pentatónica mayor",
        "Una 8va",
      ] as const,
    },
    2: {
      tonicMaxFret: 8,
      triadMaxFret: 7,
      pentatonicMaxFret: 8,
      octaveMaxFret: 15,
      desc: "Hasta el 8vo traste",
      stepDesc: [
        "Tónica hasta el 8vo traste",
        "Triadas hasta el 7mo traste",
        "Pentatónica mayor",
        "Dos octavas",
      ] as const,
    },
    3: {
      tonicMaxFret: 12,
      triadMaxFret: 12,
      pentatonicMaxFret: 12,
      octaveMaxFret: 15,
      desc: "Hasta el 12vo traste",
      stepDesc: [
        "Tónica hasta el 12vo traste",
        "Triadas hasta el 12vo traste",
        "Pentatónica mayor",
        "Ida y vuelta",
      ] as const,
    },
  };

  // ─── Sistema de misiones de práctica ───
  const [practiceMission, setPracticeMission] = useState<PracticeMission | null>(null);
  const practiceMissionRef = useRef<PracticeMission | null>(null);
  useEffect(() => { practiceMissionRef.current = practiceMission; }, [practiceMission]);
  const [practiceMissionsCompleted, setPracticeMissionsCompleted] = useState(0);
  const missionIdRef = useRef(0);

  const generateMission = useCallback(() => {
    const pool = LEVEL_NOTE_POOLS[pedagogicalLevel];
    const root = pool[Math.floor(Math.random() * pool.length)];
    const step = (Math.floor(Math.random() * 4) + 1) as 1 | 2 | 3 | 4;

    let missionScaleType = "major";
    if (step === 3) missionScaleType = "pentatonic_major";
    else if (step === 4 && pedagogicalLevel === 2) missionScaleType = "minor";

    const stepNames = [
      "Tónica",
      "Triada",
      "Pentatónica",
      pedagogicalLevel === 3 ? "Ida y vuelta" : pedagogicalLevel === 2 ? "Dos octavas" : "Una 8va",
    ];
    const scaleLabel = missionScaleType === "pentatonic_major" ? "Pentatónica Mayor" : missionScaleType === "minor" ? "Menor Natural" : "Mayor";

    missionIdRef.current += 1;
    const mission: PracticeMission = {
      id: missionIdRef.current,
      root,
      scaleType: missionScaleType,
      step,
      title: `${root} ${scaleLabel}`,
      instruction: `Paso ${step}: ${stepNames[step - 1]}`,
    };

    setPracticeMission(mission);
    setRootNote(root);
    setScaleType(missionScaleType);
    setFocusLevel(step);
    // Limpiar triadas al cambiar misión
    setTriadGroups([]);
    setCurrentTriadSelection([]);
  }, [pedagogicalLevel]);

  // Refs para lectura estable dentro de callbacks
  const modeRef = useRef(mode);
  const currentSeqIndexRef = useRef(currentSeqIndex);

  useEffect(() => { modeRef.current = mode; }, [mode]);

  // Colapsar sidebar automáticamente en modo práctica
  useEffect(() => {
    if (mode === "practice") {
      setLeftCollapsed(true);
    }
  }, [mode, setLeftCollapsed]);

  // Temporizador de práctica
  useEffect(() => {
    if (!practiceStartTime) { setPracticeElapsed(0); return; }
    const id = setInterval(() => setPracticeElapsed(Math.floor((Date.now() - practiceStartTime) / 1000)), 1000);
    return () => clearInterval(id);
  }, [practiceStartTime]);

  // Focus progresivo automático en modo práctica (respeta focusLevel del usuario)
  useEffect(() => {
    if (mode !== "practice" || !isPlaying) return;
    // No forzamos focusLevel: el usuario elige el paso en el sidebar
  }, [currentSeqIndex, mode, isPlaying]);

  useEffect(() => { currentSeqIndexRef.current = currentSeqIndex; }, [currentSeqIndex]);

  useEffect(() => {
    if (pattern === "handFriendly" || pattern === "boxConnection") {
      setPattern("linear");
      setDirection("asc");
    }
  }, [pattern]);

  // ─── Datos derivados ───
  const scalePattern = useMemo(
    () => buildScalePattern(rootNote, scaleType, 15, stringCount),
    [rootNote, scaleType, stringCount]
  );

  const fingerings = useMemo(
    () => generateScaleFingerings(rootNote, scaleType, 15),
    [rootNote, scaleType]
  );

  const sandboxNpsPatterns = useMemo(
    () => (isSandbox ? selectCanonical3NpsFingerings(fingerings) : []),
    [isSandbox, fingerings]
  );

  const activeSandboxNps = useMemo(() => {
    if (sandboxNpsSelection === "full") return null;
    return (
      sandboxNpsPatterns.find((f) => f.id === sandboxNpsSelection) ??
      sandboxNpsPatterns[0] ??
      null
    );
  }, [sandboxNpsSelection, sandboxNpsPatterns]);

  const sandboxSpotlightKeys = useMemo(() => {
    if (!isSandbox || sandboxNpsSelection === "full" || !activeSandboxNps) return undefined;
    return fingeringNoteKeys(activeSandboxNps);
  }, [isSandbox, sandboxNpsSelection, activeSandboxNps]);

  const sandboxPlaybackSequence = useMemo(() => {
    if (!isSandbox || sandboxNpsSelection === "full" || !activeSandboxNps) return null;
    return fingeringToPlaybackSequence(activeSandboxNps, rootNote, sandboxPlaybackRange);
  }, [isSandbox, sandboxNpsSelection, activeSandboxNps, rootNote, sandboxPlaybackRange]);

  useEffect(() => {
    if (!isSandbox) return;
    setSandboxNpsSelection("full");
    setCurrentSeqIndex(0);
    setIsPlaying(false);
    setActiveNote(null);
  }, [isSandbox, rootNote, scaleType]);

  useEffect(() => {
    if (!isSandbox) return;
    setCurrentSeqIndex(0);
    setActiveNote(null);
    setIsPlaying(false);
  }, [isSandbox, sandboxNpsSelection]);

  useEffect(() => {
    if (!isSandbox) return;
    setCurrentSeqIndex(0);
    setActiveNote(null);
    setIsPlaying(false);
  }, [isSandbox, sandboxPlaybackRange]);

  const cagedPatterns = useMemo(
    () => generateCagedPatterns(rootNote, scaleType, 15),
    [rootNote, scaleType]
  );

  const cagedConnections = useMemo(
    () => findPatternConnections(cagedPatterns),
    [cagedPatterns]
  );

  // Geometría compartida para alinear barra CAGED con el mástil SVG
  const neckGeometry = useMemo(() => {
    return getNeckGeometry({
      frets: instrumentConfig.fretCount,
      scaleLength: instrumentConfig.scaleLength,
      targetBoardWidth: instrumentConfig.targetBoardWidth,
      paddingLeft: instrumentConfig.paddingLeft,
    });
  }, [instrumentConfig]);

  // ─── Posiciones de todas las tónicas en el mástil (para modo descubrimiento) ───
  const allTonicPositions = useMemo(() => {
    const positions: NeckPosition[] = [];
    const maxFret = LEVEL_CONFIG[pedagogicalLevel].tonicMaxFret;
    for (let s = 1; s <= stringCount; s++) {
      for (let f = 0; f <= frets; f++) {
        if (f > maxFret) continue;
        const n = getNoteAtPosition(s, f);
        if (n.note === rootNote) {
          positions.push({
            string: s,
            fret: f,
            note: n.note,
            degree: "R",
            highlight: "root" as const,
          });
        }
      }
    }
    return positions;
  }, [rootNote, pedagogicalLevel, stringCount, frets]);

  // ─── Secuencia de reproducción ───
  const sequence = useMemo(() => {
    // Camino personalizado tiene prioridad
    if (customSequence.length > 0) return customSequence;

    const cagedSelected = selectedPatterns.filter((p) => p.type === 'caged');
    const fingeringSelected = selectedPatterns.filter((p) => p.type === 'fingering');

    // Patrones CAGED seleccionados
    if (cagedSelected.length > 0) {
      const selected = cagedSelected
        .map((fp) => cagedPatterns.find((cp) => `caged-${cp.patternNumber}` === fp.id))
        .filter(Boolean) as CagedPattern[];
      if (selected.length > 0) {
        return buildCagedMixedSequence(selected, {
          direction,
          octaves,
          connectionMode: selected.length > 1 ? "overlap" : "full",
        });
      }
    }

    // MODO: Cajas seleccionadas
    if (fingeringSelected.length > 0) {
      if (pattern === "boxConnection" || fingeringSelected.length > 1) {
        return buildBoxConnectionSequence(fingeringSelected, { direction, octaves });
      }

      // Una sola caja, patrón manual
      const f = fingeringSelected[0];
      const notesWithMidi = f.notes.map((n) => ({
        ...n,
        midi: getNoteAtPosition(n.string, n.fret).midi,
      }));
      notesWithMidi.sort((a, b) => a.midi - b.midi);
      const numNotes = notesWithMidi.length;

      let indices: number[] = [];
      if (pattern === "handFriendly" || pattern === "linear") {
        for (let oct = 0; oct < octaves; oct++) {
          if (direction === "asc" || direction === "both") {
            for (let i = 0; i < numNotes; i++) indices.push(i);
          }
          if (direction === "desc" || direction === "both") {
            const start = direction === "both" ? numNotes - 2 : numNotes - 1;
            for (let i = start; i >= 0; i--) indices.push(i);
          }
        }
      } else if (pattern === "thirds") {
        for (let oct = 0; oct < octaves; oct++) {
          for (let i = 0; i < numNotes; i++) indices.push(i, (i + 2) % numNotes);
        }
      } else if (pattern === "fourths") {
        for (let oct = 0; oct < octaves; oct++) {
          for (let i = 0; i < numNotes; i++) indices.push(i, (i + 3) % numNotes);
        }
      } else if (pattern === "fifths") {
        for (let oct = 0; oct < octaves; oct++) {
          for (let i = 0; i < numNotes; i++) indices.push(i, (i + 4) % numNotes);
        }
      } else {
        for (let i = 0; i < numNotes; i++) indices.push(i);
      }

      if (pattern !== "handFriendly" && pattern !== "linear") {
        if (direction === "desc") indices = [...indices].reverse();
        else if (direction === "both") indices = [...indices, ...[...indices].reverse().slice(1, -1)];
      }

      return indices
        .map((idx) => notesWithMidi[idx % numNotes])
        .filter(Boolean)
        .map((n) => ({
          string: n.string,
          fret: n.fret,
          note: n.note,
          degree: n.degree as any,
          highlight:
            n.degree === "R"
              ? ("root" as const)
              : n.degree === "3" || n.degree === "b3"
              ? ("third" as const)
              : n.degree === "5"
              ? ("fifth" as const)
              : ("scale" as const),
          finger: n.finger,
        }));
    }

    // MODO: Libre (sin cajas seleccionadas)
    const startOpts =
      !startFromRoot && startPosition
        ? { startString: startPosition.string, startFret: startPosition.fret }
        : {};

    if (pattern === "handFriendly") {
      return buildScaleHandFriendlySequence(rootNote, scaleType, {
        octaves,
        direction,
        maxFretSpan: 5,
        stringCount,
        includeOctave: focusLevel >= 3,
        ...startOpts,
      });
    }
    if (pattern === "boxConnection") {
      return buildBoxConnectionSequence(fingerings, { direction, octaves });
    }
    return buildScaleSequence(rootNote, scaleType, {
      octaves,
      direction,
      pattern: pattern as any,
      stringCount,
      includeOctave: focusLevel >= 3,
      ...startOpts,
    });
  }, [
    rootNote,
    scaleType,
    octaves,
    direction,
    pattern,
    selectedPatterns,
    fingerings,
    startFromRoot,
    startPosition,
    customSequence,
    cagedPatterns,
  ]);

  // Secuencia pentatónica para Paso 3
  const pentatonicSequence = useMemo(() => {
    if (focusLevel !== 3) return [];
    const cfg = LEVEL_CONFIG[pedagogicalLevel];
    return buildScaleHandFriendlySequence(rootNote, "pentatonic_major", {
      octaves: 1,
      direction: "asc",
      maxFretSpan: 5,
      stringCount,
      includeOctave: true,
    }).filter((p) => p.fret <= cfg.pentatonicMaxFret);
  }, [rootNote, pedagogicalLevel, focusLevel, stringCount]);

  // ─── Secuencias CAGED pedagógicas por nivel ───

  // Nivel 1 Paso 4: una octava ascendente dentro del patrón CAGED más bajo
  const cagedLevel1Sequence = useMemo(() => {
    if (pedagogicalLevel !== 1 || focusLevel !== 4) return [];
    const patterns = generateCagedPatterns(rootNote, scaleType, LEVEL_CONFIG[1].octaveMaxFret);
    if (patterns.length === 0) return [];
    return buildCagedMixedSequence([patterns[0]], {
      direction: "asc",
      octaves: 1,
      connectionMode: "full",
    });
  }, [rootNote, scaleType, pedagogicalLevel, focusLevel]);

  // Nivel 2 Paso 4: dos octavas ascendentes usando patrones CAGED extendidos
  const cagedLevel2Sequence = useMemo(() => {
    if (pedagogicalLevel !== 2 || focusLevel !== 4) return [];
    const extended = generateExtendedCagedPatterns(rootNote, scaleType, 2, 15);
    if (extended.length === 0) return [];
    return buildCagedMixedSequence(extended, {
      direction: "asc",
      octaves: 1,
      connectionMode: "overlap",
    });
  }, [rootNote, scaleType, pedagogicalLevel, focusLevel]);

  // Nivel 3 Paso 4: ida y vuelta por todos los patrones CAGED
  const cagedLevel3Sequence = useMemo(() => {
    if (pedagogicalLevel !== 3 || focusLevel !== 4) return [];
    const patterns = generateCagedPatterns(rootNote, scaleType, 15);
    if (patterns.length === 0) return [];
    return buildCagedMixedSequence(patterns, {
      direction: "both",
      octaves: 1,
      connectionMode: "overlap",
    });
  }, [rootNote, scaleType, pedagogicalLevel, focusLevel]);

  // Auto-seleccionar patrones CAGED según nivel pedagógico
  useEffect(() => {
    if (focusLevel === 4) {
      if (pedagogicalLevel === 1) {
        const patterns = generateCagedPatterns(rootNote, scaleType, LEVEL_CONFIG[1].octaveMaxFret);
        setSelectedPatterns(patterns.slice(0, 1).map((p) => toFretboardPattern(p)));
      } else if (pedagogicalLevel === 2) {
        const extended = generateExtendedCagedPatterns(rootNote, scaleType, 2, 15);
        setSelectedPatterns(extended.map((p) => toFretboardPattern(p)));
      } else {
        const patterns = generateCagedPatterns(rootNote, scaleType, 15);
        setSelectedPatterns(patterns.map((p) => toFretboardPattern(p)));
      }
    } else {
      // Pasos 1-3: progresión gradual de patrones según nivel
      const maxFret = LEVEL_CONFIG[pedagogicalLevel].octaveMaxFret;
      const patterns = generateCagedPatterns(rootNote, scaleType, maxFret);
      if (pedagogicalLevel === 1) {
        setSelectedPatterns(patterns.slice(0, 1).map((p) => toFretboardPattern(p)));
      } else if (pedagogicalLevel === 2) {
        setSelectedPatterns(patterns.slice(0, Math.min(3, patterns.length)).map((p) => toFretboardPattern(p)));
      } else {
        setSelectedPatterns(patterns.map((p) => toFretboardPattern(p)));
      }
    }
  }, [rootNote, scaleType, pedagogicalLevel, focusLevel]);

  const pianoIntervalDirForLearn = useMemo((): PianoIntervalDirection | undefined => {
    if (!pianoLearnActive && !showPianoLearnIntro && !showPianoIntervalTransition) {
      return undefined;
    }
    if (focusLevel === 2 || focusLevel === 3) return pianoLearnIntervalDir;
    return undefined;
  }, [
    pianoLearnActive,
    showPianoLearnIntro,
    showPianoIntervalTransition,
    focusLevel,
    pianoLearnIntervalDir,
  ]);

  const pianoFingering = useMemo(() => {
    if (surface !== "keyboard") return null;
    if (
      pedagogicalLevel === 1 &&
      focusLevel <= 5 &&
      (pianoLearnActive || showPianoLearnIntro || showPianoIntervalTransition)
    ) {
      return buildPianoLearnKeyboardHighlights(
        rootNote,
        scaleType,
        focusLevel as PianoFocusStep,
        pianoIntervalDirForLearn
      );
    }
    return buildPianoHighlights(
      rootNote,
      scaleType,
      pedagogicalLevel,
      focusLevel,
      currentSeqIndex,
      isPlaying
    );
  }, [
    surface,
    rootNote,
    scaleType,
    pedagogicalLevel,
    focusLevel,
    currentSeqIndex,
    isPlaying,
    pianoLearnActive,
    showPianoLearnIntro,
    showPianoIntervalTransition,
    pianoIntervalDirForLearn,
  ]);

  const midiToNeck = useCallback((midi: number, degree = "R"): NeckPosition => {
    const { string, fret, note } = midiToNoteAtPosition(midi);
    return {
      string,
      fret,
      note,
      degree: degree as NeckPosition["degree"],
    };
  }, []);

  // Filtra la secuencia según paso y nivel pedagógico
  const filteredSequence = useMemo(() => {
    if (surface === "keyboard" && pedagogicalLevel === 1 && focusLevel <= 5) {
      return buildPianoLevel1PlaybackMidis(
        rootNote,
        scaleType,
        focusLevel as PianoFocusStep
      ).map((midi) => midiToNeck(midi));
    }

    const cfg = LEVEL_CONFIG[pedagogicalLevel];

    // Paso 1: Tónica
    if (focusLevel === 1) {
      if (sequence.length === 0) return [];
      return sequence.filter((p) => p.degree === "R" && p.fret <= cfg.tonicMaxFret);
    }

    // Paso 2: Triada
    if (focusLevel === 2) {
      if (sequence.length === 0) return [];
      return sequence.filter(
        (p) =>
          (p.degree === "R" || p.degree === "3" || p.degree === "b3" || p.degree === "5") &&
          p.fret <= cfg.triadMaxFret
      );
    }

    // Paso 3: Pentatónica mayor
    if (focusLevel === 3) {
      return pentatonicSequence;
    }

    // Paso 4: Una 8va / Completa
    if (focusLevel === 4) {
      // Nivel 1: una octava CAGED
      if (pedagogicalLevel === 1 && cagedLevel1Sequence.length > 0) {
        return cagedLevel1Sequence;
      }
      // Nivel 2: dos octavas ascendentes CAGED
      if (pedagogicalLevel === 2 && cagedLevel2Sequence.length > 0) {
        return cagedLevel2Sequence;
      }
      // Nivel 3: ida y vuelta CAGED
      if (pedagogicalLevel === 3 && cagedLevel3Sequence.length > 0) {
        return cagedLevel3Sequence;
      }
      // Fallback
      if (sequence.length === 0) return [];
      return sequence;
    }

    return sequence;
  }, [
    sequence,
    focusLevel,
    pedagogicalLevel,
    pentatonicSequence,
    cagedLevel1Sequence,
    cagedLevel2Sequence,
    cagedLevel3Sequence,
    surface,
    rootNote,
    scaleType,
    midiToNeck,
  ]);

  // Secuencia que se usa para reproducción y práctica (contextual)
  const playbackSequence = useMemo(() => {
    if (isSandbox && sandboxPlaybackSequence) return sandboxPlaybackSequence;
    return filteredSequence.length > 0 ? filteredSequence : sequence;
  }, [isSandbox, sandboxPlaybackSequence, filteredSequence, sequence]);

  const playbackSequenceRef = useRef(playbackSequence);
  useEffect(() => { playbackSequenceRef.current = playbackSequence; }, [playbackSequence]);

  const staffHighlightNote = useMemo(() => {
    if (!isSandbox) return activeNote;
    if (isPlaying && playbackSequence.length > 0 && currentSeqIndex >= 0) {
      const p = playbackSequence[currentSeqIndex];
      if (p) return { string: p.string, fret: p.fret };
    }
    return staffHoverNote ?? activeNote;
  }, [
    isSandbox,
    isPlaying,
    playbackSequence,
    currentSeqIndex,
    staffHoverNote,
    activeNote,
  ]);

  // Ayuda mnemotécnica para el salto actual
  const mnemonicTip = useMemo(() => {
    if (!isPlaying || currentSeqIndex >= playbackSequence.length - 1) return null;
    const current = playbackSequence[currentSeqIndex];
    const next = playbackSequence[currentSeqIndex + 1];
    if (!current || !next || current.string === next.string) return null;
    const jump = JUMP_GRAPH[current.string];
    if (jump && jump.to === next.string) {
      return {
        label: jump.label,
        arrow: jump.arrow,
        fromString: STRING_NAMES[current.string] || `Cuerda ${current.string}`,
        toString: STRING_NAMES[next.string] || `Cuerda ${next.string}`,
        fromFret: current.fret,
        toFret: next.fret,
      };
    }
    return {
      label: "cambio de cuerda",
      arrow: "→",
      fromString: STRING_NAMES[current.string] || `Cuerda ${current.string}`,
      toString: STRING_NAMES[next.string] || `Cuerda ${next.string}`,
      fromFret: current.fret,
      toFret: next.fret,
    };
  }, [isPlaying, currentSeqIndex, playbackSequence]);

  // ─── Detección en tiempo real con YIN (modo práctica) ───
  const sequenceRef = useRef(sequence);
  useEffect(() => { sequenceRef.current = sequence; }, [sequence]);

  const isPlayingRef = useRef(isPlaying);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  const pentatonicPlayModeRef = useRef(pentatonicMissionMode && pentatonicSubStep === 3);
  useEffect(() => { pentatonicPlayModeRef.current = pentatonicMissionMode && pentatonicSubStep === 3; }, [pentatonicMissionMode, pentatonicSubStep]);

  const pentatonicPlayIndexRef = useRef(pentatonicPlayIndex);
  useEffect(() => { pentatonicPlayIndexRef.current = pentatonicPlayIndex; }, [pentatonicPlayIndex]);

  const pentatonicPlayTargetRef = useRef(pentatonicPlayTarget);
  useEffect(() => { pentatonicPlayTargetRef.current = pentatonicPlayTarget; }, [pentatonicPlayTarget]);

  const octavePlayModeRef = useRef(octaveMissionMode && octaveSubStep === 3);
  useEffect(() => { octavePlayModeRef.current = octaveMissionMode && octaveSubStep === 3; }, [octaveMissionMode, octaveSubStep]);

  const octavePlayIndexRef = useRef(octavePlayIndex);
  useEffect(() => { octavePlayIndexRef.current = octavePlayIndex; }, [octavePlayIndex]);

  const octavePlayTargetRef = useRef(octavePlayTarget);
  useEffect(() => { octavePlayTargetRef.current = octavePlayTarget; }, [octavePlayTarget]);

  const handleNoteDetected = useCallback((note: NoteName, _octave: number, _frequency: number) => {
    // eslint-disable-next-line no-console
    console.log("[handleNoteDetected] isPlaying:", isPlayingRef.current, "pentatonicPlayMode:", pentatonicPlayModeRef.current, "note:", note, "octave:", _octave, "freq:", _frequency);
    if (!isPlayingRef.current) {
      // eslint-disable-next-line no-console
      console.log("[handleNoteDetected] SKIP: isPlaying is false");
      return;
    }

    // Modo pentatónico micrófono
    if (pentatonicPlayModeRef.current) {
      const idx = pentatonicPlayIndexRef.current;
      const target = pentatonicPlayTargetRef.current[idx];
      // eslint-disable-next-line no-console
      console.log("[handleNoteDetected] pentatonic mode. idx:", idx, "target:", target, "playTarget length:", pentatonicPlayTargetRef.current.length);
      if (!target) {
        // eslint-disable-next-line no-console
        console.log("[handleNoteDetected] SKIP: no target at idx", idx);
        return;
      }

      const targetPC = noteToSemitones(target.note);
      const detectedPC = noteToSemitones(note);
      // eslint-disable-next-line no-console
      console.log("[handleNoteDetected] targetPC:", targetPC, "detectedPC:", detectedPC, "target.note:", target.note, "detected.note:", note);

      if (detectedPC === targetPC) {
        setLearnFeedback("success");
        setTimeout(() => setLearnFeedback(null), 700);
        const nextIdx = idx + 1;
        setPentatonicPlayIndex(nextIdx);
        // Resaltar la siguiente nota por tocar
        const nextTarget = pentatonicPlayTargetRef.current[nextIdx];
        if (nextTarget) {
          setActiveNote({ string: nextTarget.string, fret: nextTarget.fret });
        }
        if (nextIdx >= pentatonicPlayTargetRef.current.length) {
          // Misión de micrófono completada
          setTimeout(() => {
            setMissionCompleteProps({
              title: "¡Pentatónica dominada!",
              message: `Tocaste correctamente todas las notas de la escala pentatónica de ${rootNote}. Ya podés usarla para improvisar y crear melodías.`,
              nextLabel: "Volver al explorador",
              nextStep: 0,
            });
            setShowMissionComplete(true);
            setPentatonicMissionMode(false);
            setCurrentMissionStep(0);
            setIsPlaying(false);
            setActiveNote(null);
          }, 800);
        }
      } else {
        setLearnFeedback("fail");
        setTimeout(() => setLearnFeedback(null), 700);
      }
      return;
    }

    // Modo octava / escala completa micrófono
    if (octavePlayModeRef.current) {
      const idx = octavePlayIndexRef.current;
      const target = octavePlayTargetRef.current[idx];
      if (!target) return;

      const targetPC = noteToSemitones(target.note);
      const detectedPC = noteToSemitones(note);

      if (detectedPC === targetPC) {
        setLearnFeedback("success");
        setTimeout(() => setLearnFeedback(null), 700);
        const nextIdx = idx + 1;
        setOctavePlayIndex(nextIdx);
        const nextTarget = octavePlayTargetRef.current[nextIdx];
        if (nextTarget) {
          setActiveNote({ string: nextTarget.string, fret: nextTarget.fret });
        }
        if (nextIdx >= octavePlayTargetRef.current.length) {
          setTimeout(() => {
            setMissionCompleteProps({
              title: "¡Escala dominada!",
              message: `Tocaste correctamente todas las notas de la escala de ${rootNote}. Ya podés usarla para improvisar y crear melodías.`,
              nextLabel: "Volver al explorador",
              nextStep: 0,
            });
            setShowMissionComplete(true);
            setOctaveMissionMode(false);
            setCurrentMissionStep(0);
            setIsPlaying(false);
            setActiveNote(null);
          }, 800);
        }
      } else {
        setLearnFeedback("fail");
        setTimeout(() => setLearnFeedback(null), 700);
      }
      return;
    }

    const idx = currentSeqIndexRef.current;
    const targetNote = playbackSequenceRef.current[idx]?.note;
    if (!targetNote) return;

    const targetPC = NOTES.indexOf(targetNote) % 12;
    const detectedPC = NOTES.indexOf(note) % 12;

    setPracticeAttempts((a) => a + 1);

    if (detectedPC === targetPC) {
      setPracticeFeedback("success");
      setPracticePhase("success");
      setPracticeScore((s) => s + 1);
      setTimeout(() => { setPracticeFeedback(null); setPracticePhase("listen"); }, 700);
      const next = idx + 1;
      if (next >= playbackSequenceRef.current.length) {
        setIsPlaying(false);
        setCurrentSeqIndex(0);
        setActiveNote(null);
        setPracticeStartTime(null);
        if (practiceMissionRef.current) {
          setPracticeMissionsCompleted((c) => c + 1);
        }
      } else {
        setCurrentSeqIndex(next);
        setActiveNote({ string: playbackSequenceRef.current[next].string, fret: playbackSequenceRef.current[next].fret });
      }
    } else {
      setPracticeFeedback("fail");
      setPracticePhase("fail");
      setTimeout(() => { setPracticeFeedback(null); setPracticePhase("listen"); }, 700);
    }
  }, [rootNote]);

  const { startListening, stopListening, state: pitchState } = usePitchDetector({
    threshold: 0.003,
    holdTimeMs: 150,
    onNoteDetected: handleNoteDetected,
  });

  const handleStartPractice = useCallback(async () => {
    setShowPracticeIntro(false);
    setCurrentSeqIndex(0);
    if (playbackSequence.length > 0) {
      setActiveNote({ string: playbackSequence[0].string, fret: playbackSequence[0].fret });
    }
    setPracticeScore(0);
    setPracticeAttempts(0);
    setPracticeStartTime(Date.now());
    setPracticePhase("listen");
    await startListening();
  }, [playbackSequence, startListening]);

  // ─── Animación / práctica ───
  useEffect(() => {
    if (!isPlaying || playbackSequence.length === 0) return;

    // No activar timer de animación ni modal de práctica durante misiones pentatónica u octava
    if (
      pentatonicMissionMode ||
      octaveMissionMode ||
      tonicDiscoveryMode ||
      triadOrderedMode ||
      triadFreeMissionMode ||
      pianoLearnActive
    ) {
      return;
    }

    if (mode === "practice") {
      setShowPracticeIntro(true);
      return () => { stopListening(); };
    }

    // Modo aprender / sandbox: timer con BPM; sandbox ancla tónica + pausa breve
    const seq = playbackSequence;
    const intervalMs = (60 / bpm) * 1000;
    const useTonicAnchor = isSandbox && Boolean(sandboxPlaybackSequence) && seq.length > 1;
    const tonicPauseMs = Math.round(intervalMs * 1.75);
    let idx = 0;
    let timeoutId: ReturnType<typeof setTimeout>;

    const tick = () => {
      if (idx >= seq.length) {
        setIsPlaying(false);
        setCurrentSeqIndex(0);
        setActiveNote(null);
        return;
      }
      const pos = seq[idx];
      setActiveNote({ string: pos.string, fret: pos.fret });
      setCurrentSeqIndex(idx);
      if (surface === "keyboard") {
        const { note, octave } = getNoteAtPosition(pos.string, pos.fret);
        playNote(note, octave, 600);
      } else {
        playFret(pos.string, pos.fret, 600);
      }
      idx++;
      const delay = useTonicAnchor && idx === 1 ? tonicPauseMs : intervalMs;
      timeoutId = setTimeout(tick, delay);
    };

    tick();
    return () => clearTimeout(timeoutId);
  }, [
    isPlaying,
    playbackSequence,
    bpm,
    mode,
    stopListening,
    playFret,
    playNote,
    surface,
    pentatonicMissionMode,
    octaveMissionMode,
    isSandbox,
    sandboxPlaybackSequence,
  ]);

  // Filtra notas de una caja CAGED para mostrar solo N octavas
  const filterCagedByOctaves = useCallback((positions: NeckPosition[], octavesCount: number): NeckPosition[] => {
    if (octavesCount <= 0 || positions.length === 0) return positions;
    const withMidi = positions.map((p) => ({
      ...p,
      midi: getNoteAtPosition(p.string, p.fret).midi,
    }));
    withMidi.sort((a, b) => a.midi - b.midi);
    const rootIndices = withMidi
      .map((n, i) => (n.degree === "R" ? i : -1))
      .filter((i) => i !== -1);
    if (rootIndices.length < 2) return positions;
    const endIdx = rootIndices[Math.min(octavesCount, rootIndices.length - 1)];
    return withMidi.slice(0, endIdx + 1);
  }, []);

  // Max traste visible según nivel y paso
  const maxDisplayFret = useMemo(() => {
    const cfg = LEVEL_CONFIG[pedagogicalLevel];
    if (focusLevel === 1) return cfg.tonicMaxFret;
    if (focusLevel === 2) return cfg.triadMaxFret;
    if (focusLevel === 3) return cfg.pentatonicMaxFret;
    return 15;
  }, [pedagogicalLevel, focusLevel]);

  // Patrón pentatónico para mostrar en el mástil (paso 3)
  const pentatonicPattern = useMemo(() => {
    if (focusLevel !== 3) return { positions: [] as NeckPosition[], notes: [] as NoteName[], degrees: [] as string[] };
    return buildScalePattern(rootNote, "pentatonic_major", maxDisplayFret, stringCount);
  }, [rootNote, focusLevel, maxDisplayFret, stringCount]);

  // Tónicas disponibles para construcción de caminos pentatónicos (hasta 3, de grave a agudo)
  const availablePentatonicTonics = useMemo(() => {
    const maxFret = LEVEL_CONFIG[pedagogicalLevel].pentatonicMaxFret;
    return allTonicPositions
      .filter((p) => p.fret <= maxFret)
      .map((p) => ({ ...p, midi: getNoteAtPosition(p.string, p.fret).midi }))
      .sort((a, b) => a.midi - b.midi)
      .slice(0, 3);
  }, [allTonicPositions, pedagogicalLevel]);

  // Tónicas disponibles para construcción de caminos de octava (hasta 3, de grave a agudo)
  const availableOctaveTonics = useMemo(() => {
    const maxFret = LEVEL_CONFIG[pedagogicalLevel].octaveMaxFret;
    return allTonicPositions
      .filter((p) => p.fret <= maxFret)
      .map((p) => ({ ...p, midi: getNoteAtPosition(p.string, p.fret).midi }))
      .sort((a, b) => a.midi - b.midi)
      .slice(0, 3);
  }, [allTonicPositions, pedagogicalLevel]);

  // Secuencia esperada de grados pentatónicos
  const pentatonicExpectedDegrees = useMemo(() => {
    if (pentatonicPattern.notes.length === 0) return [] as string[];
    return ["R", "2", "3", "5", "6"];
  }, [pentatonicPattern]);

  // ─── Posiciones para el mástil / teclado ───
  const highlightedPositions = useMemo(() => {
    if (isSandbox) {
      const base = scalePattern.positions
        .filter((p) => p.fret <= maxDisplayFret)
        .map((p) => ({
          ...p,
          highlight: (p.degree === "R" ? "root" : "scale") as NeckPosition["highlight"],
        }));
      if (!isPlaying || playbackSequence.length === 0) return base;
      const active = playbackSequence[currentSeqIndex];
      return base.map((p) => {
        if (active && p.string === active.string && p.fret === active.fret) {
          return { ...p, highlight: "active" as const };
        }
        return p;
      });
    }

    if (pianoFingering && surface === "keyboard") {
      return pianoFingering.map((f) => {
        const { string, fret, note } = midiToNoteAtPosition(f.midi);
        return {
          string,
          fret,
          note,
          degree: f.degree.replace("*", "") as NeckPosition["degree"],
          finger: f.finger,
          hand: f.hand === "L" ? ("left" as const) : ("right" as const),
        };
      });
    }

    // MODO DESCUBRIMIENTO: Solo mostrar tónicas ya descubiertas
    if (tonicDiscoveryMode) {
      return allTonicPositions
        .filter((p) => discoveredTonics.has(`${p.string}-${p.fret}`))
        .map((p) => ({ ...p, highlight: "root" as const }));
    }

    // MODO TRIADAS ORDENADAS: Solo notas de la triada (R, 3ra, 5ta)
    if (triadOrderedMode) {
      return scalePattern.positions
        .filter((p) => p.fret <= maxDisplayFret)
        .filter((p) => p.degree === "R" || p.degree === "3" || p.degree === "b3" || p.degree === "5")
        .map((p) => ({ ...p }));
    }

    // MODO TRIADAS LIBRES (misión): Solo notas de la triada
    if (triadFreeMissionMode) {
      return scalePattern.positions
        .filter((p) => p.fret <= maxDisplayFret)
        .filter((p) => p.degree === "R" || p.degree === "3" || p.degree === "b3" || p.degree === "5")
        .map((p) => ({ ...p }));
    }

    // MODO PENTATÓNICO (misión paso 3): mostrar notas pentatónicas
    if (pentatonicMissionMode) {
      // Sub-misión 4 (micrófono): mostrar la nota actual a tocar
      if (pentatonicSubStep === 3) {
        const currentPlay = pentatonicPlayTarget[pentatonicPlayIndex];
        if (currentPlay) {
          return [{ ...currentPlay, highlight: "active" as const }];
        }
        return [];
      }
      // Sub-misiones 1-3 (construcción)
      const startTonic = availablePentatonicTonics[pentatonicSubStep];
      const visible = new Set<string>();
      const result: NeckPosition[] = [];
      if (startTonic) {
        // Tónica de inicio siempre visible
        result.push({ ...startTonic, highlight: "root" as const });
        visible.add(`${startTonic.string}-${startTonic.fret}`);
      }
      // Notas ya seleccionadas en el camino actual
      pentatonicPathSelection.forEach((p) => {
        const key = `${p.string}-${p.fret}`;
        if (!visible.has(key)) {
          result.push({ ...p, highlight: "scale" as const });
          visible.add(key);
        }
      });
      return result;
    }

    // MODO OCTAVA / ESCALA COMPLETA (misión paso 4): mostrar notas de la escala
    if (octaveMissionMode) {
      // Sub-misión 4 (micrófono): mostrar la nota actual a tocar
      if (octaveSubStep === 3) {
        const currentPlay = octavePlayTarget[octavePlayIndex];
        if (currentPlay) {
          return [{ ...currentPlay, highlight: "active" as const }];
        }
        return [];
      }
      // Sub-misiones 1-3 (construcción)
      const startTonic = availableOctaveTonics[octaveSubStep];
      const visible = new Set<string>();
      const result: NeckPosition[] = [];
      if (startTonic) {
        result.push({ ...startTonic, highlight: "root" as const });
        visible.add(`${startTonic.string}-${startTonic.fret}`);
      }
      octavePathSelection.forEach((p) => {
        const key = `${p.string}-${p.fret}`;
        if (!visible.has(key)) {
          result.push({ ...p, highlight: "scale" as const });
          visible.add(key);
        }
      });
      return result;
    }

    // MODO UNIFICADO: Patrones seleccionados (CAGED o fingering)
    if (selectedPatterns.length > 0) {
      const allPositions: NeckPosition[] = [];
      selectedPatterns.forEach((fp, boxIdx) => {
        const boxPositions = fp.notes.map((n) => ({
          string: n.string,
          fret: n.fret,
          note: n.note,
          degree: n.degree as any,
          highlight: n.degree === "R" ? ("root" as const) : ("scale" as const),
          finger: n.finger,
        }));
        const filtered = filterCagedByOctaves(boxPositions, octaves);
        allPositions.push(...filtered);
      });

      if (isPlaying && sequence.length > 0) {
        return [
          ...allPositions,
          ...sequence.map((p, i) => ({
            ...p,
            highlight: (i === currentSeqIndex ? "active" : p.highlight) as NeckPosition["highlight"],
          })),
        ];
      }
      return allPositions;
    }

    // MODO: Libre
    // Paso 3: Pentatónica mayor
    if (focusLevel === 3) {
      const base = pentatonicPattern.positions.filter((p) => p.fret <= maxDisplayFret).map((p) => ({ ...p }));
      if (isPlaying && playbackSequence.length > 0) {
        return [
          ...base,
          ...playbackSequence.map((p, i) => ({
            ...p,
            highlight: (i === currentSeqIndex ? "active" : "scale") as NeckPosition["highlight"],
          })),
        ];
      }
      return base;
    }

    // Nivel 1 Paso 4: mostrar secuencia CAGED de una octava
    if (pedagogicalLevel === 1 && focusLevel === 4 && cagedLevel1Sequence.length > 0) {
      if (isPlaying) {
        return cagedLevel1Sequence.map((p, i) => ({
          ...p,
          highlight: (i === currentSeqIndex ? "active" : p.highlight || "scale") as NeckPosition["highlight"],
        }));
      }
      return cagedLevel1Sequence.map((p) => ({ ...p }));
    }

    // Nivel 2 Paso 4: mostrar secuencia CAGED de dos octavas
    if (pedagogicalLevel === 2 && focusLevel === 4 && cagedLevel2Sequence.length > 0) {
      if (isPlaying) {
        return cagedLevel2Sequence.map((p, i) => ({
          ...p,
          highlight: (i === currentSeqIndex ? "active" : p.highlight || "scale") as NeckPosition["highlight"],
        }));
      }
      return cagedLevel2Sequence.map((p) => ({ ...p }));
    }

    // Nivel 3 Paso 4: mostrar secuencia CAGED ida y vuelta
    if (pedagogicalLevel === 3 && focusLevel === 4 && cagedLevel3Sequence.length > 0) {
      if (isPlaying) {
        return cagedLevel3Sequence.map((p, i) => ({
          ...p,
          highlight: (i === currentSeqIndex ? "active" : p.highlight || "scale") as NeckPosition["highlight"],
        }));
      }
      return cagedLevel3Sequence.map((p) => ({ ...p }));
    }

    const base = scalePattern.positions.filter((p) => p.fret <= maxDisplayFret).map((p) => ({ ...p }));
    if (isPlaying && playbackSequence.length > 0) {
      return [
        ...base,
        ...playbackSequence.map((p, i) => ({
          ...p,
          highlight: (i === currentSeqIndex ? "active" : "scale") as NeckPosition["highlight"],
        })),
      ];
    }
    return base;
  }, [
    isPlaying,
    playbackSequence,
    currentSeqIndex,
    scalePattern.positions,
    selectedPatterns,
    fingerings,
    pedagogicalLevel,
    focusLevel,
    maxDisplayFret,
    pentatonicPattern,
    cagedLevel1Sequence,
    cagedLevel2Sequence,
    cagedLevel3Sequence,
    cagedPatterns,
    sequence,
    tonicDiscoveryMode,
    discoveredTonics,
    allTonicPositions,
    triadOrderedMode,
    triadFreeMissionMode,
    pianoFingering,
    surface,
    isSandbox,
    maxDisplayFret,
  ]);

  const selectedDegree = useMemo(() => {
    if (focusedDegree) return focusedDegree;
    if (!activeNote) return null;
    const pos = highlightedPositions.find(
      (p) => p.string === activeNote.string && p.fret === activeNote.fret
    );
    return pos?.degree ?? null;
  }, [focusedDegree, activeNote, highlightedPositions]);

  const handleStaffNoteSelect = useCallback(
    (ev: PracticeNoteEvent) => {
      if (ev.degree) setFocusedDegree(ev.degree);
      const pos =
        highlightedPositions.find((p) => p.degree === ev.degree) ||
        findNeckPositionForMidi(highlightedPositions, ev.midi);
      if (pos) {
        setActiveNote({ string: pos.string, fret: pos.fret });
        playFret(pos.string, pos.fret);
        return;
      }
      const { string, fret } = midiToNoteAtPosition(ev.midi);
      setActiveNote({ string, fret });
      playFret(string, fret);
    },
    [highlightedPositions, playFret]
  );

  const scaleDegreeChips = useMemo(
    () =>
      scalePattern.notes.map((note, idx) => ({
        degree: scalePattern.degrees[idx] ?? "?",
        note,
      })),
    [scalePattern]
  );

  const selectedScaleNoteName = useMemo(() => {
    if (!focusedDegree) return null;
    const idx = scalePattern.degrees.indexOf(focusedDegree);
    return idx >= 0 ? scalePattern.notes[idx] : null;
  }, [focusedDegree, scalePattern]);

  const handlePianoDegreeSelect = useCallback(
    (degree: string) => {
      setFocusedDegree(degree);
      const key =
        pianoFingering?.find((k) => k.degree.replace("*", "") === degree.replace("*", "")) ??
        pianoFingering?.find((k) => k.degree === degree);
      if (key) {
        const { string, fret } = midiToNoteAtPosition(key.midi);
        setActiveNote({ string, fret });
        const octave = Math.floor(key.midi / 12) - 1;
        playNote(key.step, octave, 500);
        return;
      }
      const pos = highlightedPositions.find((p) => p.degree === degree);
      if (pos) {
        setActiveNote({ string: pos.string, fret: pos.fret });
        playFret(pos.string, pos.fret, 500);
      }
    },
    [pianoFingering, highlightedPositions, playNote, playFret]
  );

  // ─── Focus visual: atenuar notas sobrantes ───
  const hasFocus = startPosition !== null || customSequence.length > 0;
  const dimmedKeys = useMemo(() => {
    if (!hasFocus) return undefined;
    const focusSet = new Set<string>();
    sequence.forEach((p) => focusSet.add(`${p.string}-${p.fret}`));
    if (startPosition) focusSet.add(`${startPosition.string}-${startPosition.fret}`);
    return focusSet;
  }, [hasFocus, sequence, startPosition]);

  // ─── Conexiones entre cajas seleccionadas ───
  // Lógica pedagógica: desde la ÚLTIMA nota de la caja A
  // hacia la TÓNICA (Raíz) de la caja B.
  // Así el estudiante sabe que al cambiar de posición debe
  // reorientarse encontrando la tónica en la nueva caja.
  const connections = useMemo(() => {
    const conns: NeckConnection[] = [];

    // En modo descubrimiento no mostrar conexiones que revelen la respuesta
    if (tonicDiscoveryMode) return conns;

    // Camino personalizado
    if (customSequence.length > 1) {
      for (let i = 0; i < customSequence.length - 1; i++) {
        const from = customSequence[i];
        const to = customSequence[i + 1];
        conns.push({
          from: { string: from.string, fret: from.fret },
          to: { string: to.string, fret: to.fret },
          color: "#D946EF",
          arrow: true,
          dashed: false,
          label: i === 0 ? "inicio" : undefined,
        });
      }
    }

    // 🔺 Rutas de grupos de triadas confirmados
    triadGroups.forEach((group) => {
      const positions = group.positions;
      for (let i = 0; i < positions.length - 1; i++) {
        conns.push({
          from: { string: positions[i].string, fret: positions[i].fret },
          to: { string: positions[i + 1].string, fret: positions[i + 1].fret },
          color: "#5BA8A8",
          arrow: true,
          dashed: false,
          label: i === 0 ? `Triada ${group.number}` : undefined,
        });
      }
      // Cerrar el triángulo: última nota → primera nota
      if (positions.length >= 2) {
        conns.push({
          from: { string: positions[positions.length - 1].string, fret: positions[positions.length - 1].fret },
          to: { string: positions[0].string, fret: positions[0].fret },
          color: "#5BA8A8",
          arrow: true,
          dashed: true,
          label: undefined,
        });
      }
    });

    // 🔗 Enlace entre triadas: conectar triadas en orden de creación
    if (triadGroups.length >= 2) {
      for (let g = 0; g < triadGroups.length - 1; g++) {
        const fromGroup = triadGroups[g];
        const toGroup = triadGroups[g + 1];
        if (fromGroup.positions.length > 0 && toGroup.positions.length > 0) {
          // Conectar desde la última nota de la triada anterior
          // hacia la primera nota de la triada siguiente
          conns.push({
            from: { string: fromGroup.positions[fromGroup.positions.length - 1].string, fret: fromGroup.positions[fromGroup.positions.length - 1].fret },
            to: { string: toGroup.positions[0].string, fret: toGroup.positions[0].fret },
            color: SATURATED_DEGREE_HEX[0],
            arrow: true,
            dashed: false,
            label: `Enlace ${g + 1}→${g + 2}`,
          });
        }
      }
    }

    // 🎯 Ruta de la Tónica: conecta solo las posiciones de la tónica
    if (routeMode === "tonic") {
      const tonicPositions: { string: number; fret: number }[] = [];
      for (let s = 1; s <= stringCount; s++) {
        for (let f = 0; f <= frets; f++) {
          const n = getNoteAtPosition(s, f);
          if (n.note === rootNote) {
            tonicPositions.push({ string: s, fret: f });
          }
        }
      }
      // Orden pedagógico: de grave a agudo (cuerda alta a baja, traste bajo a alto)
      tonicPositions.sort((a, b) => {
        if (a.fret !== b.fret) return a.fret - b.fret;
        return a.string - b.string;
      });
      // Regla de oro: máximo 10 líneas
      if (tonicPositions.length > 1 && tonicPositions.length <= 10) {
        for (let i = 0; i < tonicPositions.length - 1; i++) {
          conns.push({
            from: tonicPositions[i],
            to: tonicPositions[i + 1],
            color: SATURATED_DEGREE_HEX[0],
            arrow: true,
            dashed: false,
            label: `C ${i + 1} de ${tonicPositions.length - 1}`,
          });
        }
      }
    }

    // 🐍 Ruta de Recorrido: solo 1 octava, patrón lineal/ida y vuelta
    const routeSeq = playbackSequence.length > 0 ? playbackSequence : sequence;
    if (routeMode === "recorrido" && routeSeq.length > 1 && routeSeq.length <= 10) {
      for (let i = 0; i < routeSeq.length - 1; i++) {
        const from = routeSeq[i];
        const to = routeSeq[i + 1];
        if (from.string === to.string && from.fret === to.fret) continue;
        conns.push({
          from: { string: from.string, fret: from.fret },
          to: { string: to.string, fret: to.fret },
          color: "#B8A99A",
          arrow: true,
          dashed: false,
          label: `${from.note} → ${to.note}`,
        });
      }
    }

    // 🔗 Conexiones CAGED entre patrones seleccionados
    const cagedSelected = selectedPatterns.filter((p) => p.type === 'caged');
    if (cagedSelected.length >= 2) {
      const selected = cagedSelected
        .map((fp) => cagedPatterns.find((cp) => `caged-${cp.patternNumber}` === fp.id))
        .filter(Boolean) as CagedPattern[];
      const cagedConns = findPatternConnections(selected);
      for (const c of cagedConns) {
        conns.push({
          from: { string: c.sharedString, fret: c.fromFret },
          to: { string: c.sharedString, fret: c.toFret },
          color: "#A07840",
          dashed: true,
          arrow: true,
          label: `${c.fromShape}→${c.toShape}`,
        });
      }
    }

    // 🔗 Ruta de Transición: entre cajas unificadas
    if (routeMode === "transition" && selectedPatterns.length >= 2) {
      const sorted = [...selectedPatterns].sort((a, b) => a.position - b.position);

      for (let i = 0; i < sorted.length - 1; i++) {
        const boxA = sorted[i];
        const boxB = sorted[i + 1];

        const notesA = boxA.notes.map((n) => ({
          ...n,
          midi: getNoteAtPosition(n.string, n.fret).midi,
        }));
        notesA.sort((a, b) => b.midi - a.midi);
        const lastNoteA = notesA[0];

        const rootNoteB = boxB.notes.find((n) => n.degree === "R");

        if (lastNoteA && rootNoteB) {
          const guide = getTransitionGuide(boxA, boxB);
          conns.push({
            from: { string: lastNoteA.string, fret: lastNoteA.fret },
            to: { string: rootNoteB.string, fret: rootNoteB.fret },
            color: "#A07840",
            dashed: true,
            arrow: true,
            label: guide.message,
          });
        }
      }
    }

    return conns;
  }, [routeMode, selectedPatterns, fingerings, customSequence, sequence, rootNote, triadGroups, cagedPatterns, tonicDiscoveryMode]);

  // ─── Posiciones de éxito para animación visual ───
  const successPositions = useMemo(() => {
    const set = new Set<string>();
    if (tonicDiscoveryMode) {
      discoveredTonics.forEach((k) => set.add(k));
    } else if (triadOrderedMode) {
      orderedSelection.forEach((p) => set.add(`${p.string}-${p.fret}`));
    } else if (triadFreeMissionMode) {
      triadGroups.forEach((g) => g.positions.forEach((p) => set.add(`${p.string}-${p.fret}`)));
      currentTriadSelection.forEach((p) => set.add(`${p.string}-${p.fret}`));
    } else if (pentatonicMissionMode) {
      pentatonicPathSelection.forEach((p) => set.add(`${p.string}-${p.fret}`));
      if (pentatonicSubStep === 3) {
        pentatonicPlayTarget.slice(0, pentatonicPlayIndex).forEach((p) => set.add(`${p.string}-${p.fret}`));
      }
    } else if (octaveMissionMode) {
      octavePathSelection.forEach((p) => set.add(`${p.string}-${p.fret}`));
      if (octaveSubStep === 3) {
        octavePlayTarget.slice(0, octavePlayIndex).forEach((p) => set.add(`${p.string}-${p.fret}`));
      }
    }
    return set;
  }, [tonicDiscoveryMode, triadOrderedMode, triadFreeMissionMode, pentatonicMissionMode, octaveMissionMode, discoveredTonics, orderedSelection, triadGroups, currentTriadSelection, pentatonicPathSelection, pentatonicPlayTarget, pentatonicPlayIndex, pentatonicSubStep, octavePathSelection, octavePlayTarget, octavePlayIndex, octaveSubStep]);

  // ─── Colores por grupo de triada ───
  const GROUP_COLORS = ["#E8486E", "#3B82F6", "#F59E0B", "#8B5CF6", "#10B981", "#EC4899"];
  const DEGREE_FILL_COLORS: Record<string, string> = {
    R: SATURATED_DEGREE_HEX[0],
    "3": SATURATED_DEGREE_HEX[2],
    b3: SATURATED_DEGREE_HEX[2],
    "5": SATURATED_DEGREE_HEX[4],
  };

  const groupedPositions = useMemo(() => {
    const map = new Map<string, { speciesFill: string; groupHalos: string[] }>();
    if (!triadOrderedMode && !triadFreeMissionMode && focusLevel !== 2) return map;

    // Primero acumulamos los colores de grupo por posición
    const groupMap = new Map<string, string[]>();

    triadGroups.forEach((group, idx) => {
      const groupColor = GROUP_COLORS[idx % GROUP_COLORS.length];
      group.positions.forEach((p) => {
        const key = `${p.string}-${p.fret}`;
        const arr = groupMap.get(key) || [];
        arr.push(groupColor);
        groupMap.set(key, arr);
      });
    });

    const inProgress = triadOrderedMode ? orderedSelection : currentTriadSelection;
    if (inProgress.length > 0) {
      const groupColor = GROUP_COLORS[triadGroups.length % GROUP_COLORS.length];
      inProgress.forEach((p) => {
        const key = `${p.string}-${p.fret}`;
        const arr = groupMap.get(key) || [];
        arr.push(groupColor);
        groupMap.set(key, arr);
      });
    }

    // Modo pentatónico: cada camino completado es un grupo
    if (pentatonicMissionMode && pentatonicSubStep < 3) {
      const groupColor = GROUP_COLORS[pentatonicSubStep % GROUP_COLORS.length];
      pentatonicPathSelection.forEach((p) => {
        const key = `${p.string}-${p.fret}`;
        const arr = groupMap.get(key) || [];
        arr.push(groupColor);
        groupMap.set(key, arr);
      });
    }

    // Modo octava: cada camino completado es un grupo
    if (octaveMissionMode && octaveSubStep < 3) {
      const groupColor = GROUP_COLORS[octaveSubStep % GROUP_COLORS.length];
      octavePathSelection.forEach((p) => {
        const key = `${p.string}-${p.fret}`;
        const arr = groupMap.get(key) || [];
        arr.push(groupColor);
        groupMap.set(key, arr);
      });
    }

    // Ahora construimos el map final con fill de especie + halos de grupo
    groupMap.forEach((halos, key) => {
      // Buscar el degree de alguna posición que tenga esta key
      let degree = "";
      for (const g of triadGroups) {
        const found = g.positions.find((p) => `${p.string}-${p.fret}` === key);
        if (found) { degree = found.degree || ""; break; }
      }
      if (!degree && inProgress) {
        const found = inProgress.find((p) => `${p.string}-${p.fret}` === key);
        if (found) degree = found.degree || "";
      }
      if (!degree && pentatonicMissionMode) {
        const found = pentatonicPathSelection.find((p) => `${p.string}-${p.fret}` === key);
        if (found) degree = found.degree || "";
      }
      if (!degree && octaveMissionMode) {
        const found = octavePathSelection.find((p) => `${p.string}-${p.fret}` === key);
        if (found) degree = found.degree || "";
      }
      map.set(key, {
        speciesFill: DEGREE_FILL_COLORS[degree] || "#FFFFFF",
        groupHalos: halos,
      });
    });

    return map;
  }, [triadGroups, currentTriadSelection, orderedSelection, triadOrderedMode, triadFreeMissionMode, focusLevel, pentatonicMissionMode, pentatonicSubStep, pentatonicPathSelection, octaveMissionMode, octaveSubStep, octavePathSelection]);

  // ─── Toggle patrón unificado ───
  const togglePattern = (pattern: FretboardPattern) => {
    setSelectedPatterns((prev) => {
      if (prev.some((p) => p.id === pattern.id)) {
        return prev.filter((p) => p.id !== pattern.id);
      }
      return [...prev, pattern];
    });
  };

  const clearPatterns = () => setSelectedPatterns([]);

  const isPatternSelected = (id: string) => selectedPatterns.some((p) => p.id === id);

  // ─── Click en mástil para elegir inicio ───
  const handleFretClick = (stringNum: number, fret: number, note: NoteName) => {
    if (isPickingStart) {
      setStartPosition({ string: stringNum, fret });
      setStartFromRoot(false);
      setIsPickingStart(false);
    }
    setActiveNote({ string: stringNum, fret });
    playFret(stringNum, fret);
    setInspectedFret({ string: stringNum, fret });

    // ─── Aprendizaje piano (exploración sin reproducción automática) ───
    if (pianoLearnActive && surface === "keyboard") {
      const stepDone = focusLevel as PianoFocusStep;
      const intervalDir =
        stepDone === 2 || stepDone === 3 ? pianoLearnIntervalDir : undefined;
      const { midi } = getNoteAtPosition(stringNum, fret);
      const targets = getPianoLearnTargetMidis(
        stepDone,
        rootNote,
        scaleType,
        intervalDir
      );
      const targetMidi = targets[0];
      const match = targetMidi != null && midi === targetMidi;

      if (match) {
        setLearnFeedback("success");
        setTimeout(() => setLearnFeedback(null), 700);

        const finishStep = () => {
          setLearnPhase("completed");
          const complete = getPianoStepCompleteContent(stepDone, rootNote, scaleLabel);
          setTimeout(() => {
            setMissionCompleteProps({
              title: complete.title,
              message: complete.message,
              steps: complete.steps,
              footerTip: complete.footerTip,
              nextLabel: complete.buttonText ?? "Siguiente paso",
              nextStep: Math.min(stepDone, 4) as 0 | 1 | 2 | 3,
            });
            setShowMissionComplete(true);
            setPianoLearnActive(false);
            setPianoLearnIntervalDir("asc");
          }, 600);
        };

        if (
          (stepDone === 2 || stepDone === 3) &&
          pianoLearnIntervalDir === "asc"
        ) {
          setPianoLearnActive(false);
          setPianoLearnFoundMidis(new Set());
          setTimeout(() => setShowPianoIntervalTransition(true), 500);
          return;
        }

        finishStep();
      } else {
        setLearnFeedback("fail");
        setTimeout(() => setLearnFeedback(null), 700);
      }
      return;
    }

    // ─── Modo descubrimiento de tónicas (Nivel 1, Paso 1) ───
    if (tonicDiscoveryMode) {
      const key = `${stringNum}-${fret}`;
      const isTonic = note === rootNote;

      if (isTonic && !discoveredTonics.has(key)) {
        setDiscoveredTonics((prev) => {
          const next = new Set(prev);
          next.add(key);
          return next;
        });
        setLearnFeedback("success");
        setTimeout(() => setLearnFeedback(null), 700);

        // Verificar si completó
        const totalTonics = allTonicPositions.length;
        const foundCount = discoveredTonics.size + 1;
        if (foundCount >= totalTonics) {
          setLearnPhase("completed");
          setTimeout(() => {
            setMissionCompleteProps({
              title: "¡Tónicas descubiertas!",
              message: `Encontraste todas las ${rootNote} en el mástil. Ahora sabés que la tónica es la nota que le da nombre a la escala y aparece en muchos lugares.`,
              nextLabel: "Descubrir triadas",
              nextStep: 1,
            });
            setShowMissionComplete(true);
            setTonicDiscoveryMode(false);
          }, 800);
        }
      } else if (!isTonic) {
        setLearnFeedback("fail");
        setTimeout(() => setLearnFeedback(null), 700);
      }
      return;
    }

    // Ctrl+Click o modo dibujar camino → agregar a secuencia personalizada
    if (isBuildingCustomPath) {
      setCustomSequence((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.string === stringNum && last.fret === fret) return prev;
        return [...prev, { string: stringNum, fret, note, highlight: "scale" as const }];
      });
    }

    // ─── Modo triadas ordenadas (misión: R → 3ra → 5ta) ───
    if (triadOrderedMode) {
      const noteInfo = getNoteAtPosition(stringNum, fret);
      const clickedNote = noteInfo.note as NoteName;

      // Fuente de verdad del paso actual
      const currentStep = orderedSelection.length; // 0 = R, 1 = 3ra, 2 = 5ta
      const expectedNotes = [scalePattern.notes[0], scalePattern.notes[2], scalePattern.notes[4]];
      const expectedDegrees = ["R", scalePattern.degrees[2], "5"];
      const expectedNote = expectedNotes[currentStep];

      // Comparar por pitch class (enharmónicos incluidos)
      const isExpected = noteToSemitones(clickedNote) === noteToSemitones(expectedNote);

      if (!isExpected) {
        setLearnFeedback("fail");
        setTimeout(() => setLearnFeedback(null), 700);
        return;
      }

      // Regla: orden ascendente de pitch
      const clickedMidi = getNoteAtPosition(stringNum, fret).midi;
      if (orderedSelection.length > 0) {
        const lastMidi = getNoteAtPosition(
          orderedSelection[orderedSelection.length - 1].string,
          orderedSelection[orderedSelection.length - 1].fret
        ).midi;
        if (clickedMidi <= lastMidi) {
          setLearnFeedback("fail");
          setTimeout(() => setLearnFeedback(null), 700);
          return;
        }
      }

      // Construir NeckPosition manualmente (puede estar fuera de scalePattern.positions)
      const clickedPos: NeckPosition = {
        string: stringNum,
        fret,
        note: clickedNote,
        degree: expectedDegrees[currentStep],
        highlight: currentStep === 0 ? "root" : currentStep === 1 ? "third" : "fifth",
      };

      const newSelection = [...orderedSelection, clickedPos];
      setOrderedSelection(newSelection);
      setLearnFeedback("success");
      setTimeout(() => setLearnFeedback(null), 700);

      if (newSelection.length === 3) {
        // Triada completada: generar hash único
        const hash = [newSelection[0], newSelection[1], newSelection[2]]
          .map((p) => `${p.string}-${p.fret}`)
          .sort()
          .join("|");

        setOrderedTriadsDone((prev) => {
          if (prev.has(hash)) return prev;
          const next = new Set(prev);
          next.add(hash);

          // Agregar a triadGroups para visualización con color
          setTriadGroups((groups) => [
            ...groups,
            { positions: newSelection, number: groups.length + 1 },
          ]);

          // Verificar si completó la misión
          if (next.size >= orderedTriadTargetCount) {
            setTimeout(() => {
              setMissionCompleteProps({
                title: "¡Triadas dominadas!",
                message: `Descubriste que el acorde de ${rootNote} Mayor se forma con las notas ${rootNote} (I), ${scalePattern.notes[2] || "3ª"} (III) y ${scalePattern.notes[4] || "5ª"} (V). Estas tres notas son la base de toda la armonía occidental.`,
                nextLabel: "Triadas libres",
                nextStep: 2,
              });
              setShowMissionComplete(true);
              setTriadOrderedMode(false);
              setOrderedSelection([]);
            }, 800);
          }

          return next;
        });

        // Reset para la siguiente triada (solo si no completó la misión)
        setOrderedSelection([]);
      }
      return;
    }

    // ─── Modo triadas libres (misión: agrupar de a 3 sin orden) ───
    if (triadFreeMissionMode) {
      const noteInfo = getNoteAtPosition(stringNum, fret);
      const clickedNote = noteInfo.note as NoteName;
      const clickedSemitone = noteToSemitones(clickedNote);

      // Notas del acorde I-III-V
      const chordNotes = [scalePattern.notes[0], scalePattern.notes[2], scalePattern.notes[4]];
      const chordDegrees = ["R", scalePattern.degrees[2], "5"];
      const chordHighlights: Array<NeckPosition["highlight"]> = ["root", "third", "fifth"];

      // Verificar si la nota clickeada pertenece al acorde (comparando pitch class)
      const chordIndex = chordNotes.findIndex((n) => noteToSemitones(n) === clickedSemitone);
      if (chordIndex === -1) {
        setLearnFeedback("fail");
        setTimeout(() => setLearnFeedback(null), 700);
        return;
      }

      // Construir NeckPosition manualmente
      const clickedPos: NeckPosition = {
        string: stringNum,
        fret,
        note: clickedNote,
        degree: chordDegrees[chordIndex],
        highlight: chordHighlights[chordIndex],
      };

      setCurrentTriadSelection((prev) => {
        if (prev.some((p) => p.string === stringNum && p.fret === fret)) return prev;
        if (prev.some((p) => noteToSemitones(p.note) === clickedSemitone)) return prev;

        const newSelection = [...prev, clickedPos];

        if (newSelection.length === 3) {
          const hasRoot = newSelection.some((p) => p.degree === "R");
          const hasThird = newSelection.some((p) => p.degree === "3" || p.degree === "b3");
          const hasFifth = newSelection.some((p) => p.degree === "5");
          if (hasRoot && hasThird && hasFifth) {
            const hash = newSelection
              .map((p) => `${p.string}-${p.fret}`)
              .sort()
              .join("|");

            setFreeTriadsDone((done) => {
              if (done.has(hash)) return done;
              const next = new Set(done);
              next.add(hash);

              setTriadGroups((groups) => [
                ...groups,
                { positions: newSelection, number: groups.length + 1 },
              ]);

              if (next.size >= freeTriadTargetCount) {
                setTimeout(() => {
                  setMissionCompleteProps({
                    title: "¡Misión completada!",
                    message: `Dominaste las triadas de ${rootNote} Mayor en todo el mástil. Ya podés reconocer las notas del acorde desde cualquier posición.`,
                    nextLabel: "Escala pentatónica",
                    nextStep: 3,
                  });
                  setShowMissionComplete(true);
                  setTriadFreeMissionMode(false);
                }, 800);
              }

              return next;
            });

            return [];
          }
        }

        return newSelection;
      });
      return;
    }

    // ─── Modo pentatónico (paso 3: construcción de caminos) ───
    if (pentatonicMissionMode && pentatonicSubStep < 3) {
      const noteInfo = getNoteAtPosition(stringNum, fret);
      const clickedNote = noteInfo.note as NoteName;
      const clickedSemitone = noteToSemitones(clickedNote);
      const clickedMidi = noteInfo.midi;

      const currentStep = pentatonicPathSelection.length; // 0-4
      const expectedDegrees = ["R", "2", "3", "5", "6"];
      const expectedDegree = expectedDegrees[currentStep];

      // Buscar si la nota clickeada tiene el grado esperado en el patrón pentatónico
      const pentatonicPos = pentatonicPattern.positions.find(
        (p) => p.string === stringNum && p.fret === fret
      );
      const isExpectedDegree = pentatonicPos?.degree === expectedDegree;

      // Para el paso 0 (R), debe coincidir con la tónica de inicio de esta sub-misión
      const startTonic = availablePentatonicTonics[pentatonicSubStep];
      if (currentStep === 0) {
        if (!startTonic || stringNum !== startTonic.string || fret !== startTonic.fret) {
          setLearnFeedback("fail");
          setTimeout(() => setLearnFeedback(null), 700);
          return;
        }
      } else if (!isExpectedDegree) {
        setLearnFeedback("fail");
        setTimeout(() => setLearnFeedback(null), 700);
        return;
      }

      // Regla: orden ascendente de pitch
      if (pentatonicPathSelection.length > 0) {
        const lastMidi = getNoteAtPosition(
          pentatonicPathSelection[pentatonicPathSelection.length - 1].string,
          pentatonicPathSelection[pentatonicPathSelection.length - 1].fret
        ).midi;
        if (clickedMidi <= lastMidi) {
          setLearnFeedback("fail");
          setTimeout(() => setLearnFeedback(null), 700);
          return;
        }
      }

      // Evitar duplicados exactos en el camino actual
      if (pentatonicPathSelection.some((p) => p.string === stringNum && p.fret === fret)) {
        setLearnFeedback("fail");
        setTimeout(() => setLearnFeedback(null), 700);
        return;
      }

      const clickedPos: NeckPosition = {
        string: stringNum,
        fret,
        note: clickedNote,
        degree: expectedDegree,
        highlight: expectedDegree === "R" ? "root" : "scale",
      };

      const newSelection = [...pentatonicPathSelection, clickedPos];
      setPentatonicPathSelection(newSelection);
      setLearnFeedback("success");
      setTimeout(() => setLearnFeedback(null), 700);

      if (newSelection.length === 5) {
        // Camino completado: guardar el camino real
        const savedPath = [...newSelection];
        const allSaved = [...pentatonicSavedPathsRef.current, savedPath];
        setPentatonicSavedPaths(allSaved);
        pentatonicSavedPathsRef.current = allSaved;
        const hash = newSelection.map((p) => `${p.string}-${p.fret}`).join("|");
        setPentatonicPathsDone((prev) => {
          const next = new Set(prev);
          next.add(hash);
          return next;
        });

        // Mostrar modal de camino completado
        const nextTonic = availablePentatonicTonics[pentatonicSubStep + 1];
        if (pentatonicSubStep < 2) {
          setPentatonicPathCompleteProps({
            title: `¡Camino ${pentatonicSubStep + 1} completado!`,
            message: `Encontraste las 5 notas de la pentatónica desde ${rootNote}. Ahora busca la siguiente tónica${nextTonic ? ` en la cuerda ${nextTonic.string}` : ""} para construir el siguiente camino.`,
            nextLabel: "Siguiente camino",
            isFinal: false,
          });
        } else {
          setPentatonicPathCompleteProps({
            title: "¡Todos los caminos completados!",
            message: `Encontraste 3 caminos de la escala pentatónica de ${rootNote}. Ahora deberás tocar las notas que encontraste en tu guitarra. El micrófono validará cada una.`,
            nextLabel: "Comenzar a tocar",
            isFinal: true,
          });
        }
        setTimeout(() => setShowPentatonicPathComplete(true), 600);
      }
      return;
    }

    // ─── Modo octava / escala completa (paso 4: construcción de caminos) ───
    if (octaveMissionMode && octaveSubStep < 3) {
      const noteInfo = getNoteAtPosition(stringNum, fret);
      const clickedNote = noteInfo.note as NoteName;
      const clickedSemitone = noteToSemitones(clickedNote);
      const clickedMidi = noteInfo.midi;

      // Calcular longitud del camino según nivel pedagógico
      const pathLength = pedagogicalLevel === 1 ? 8 : pedagogicalLevel === 2 ? 15 : 8;
      const currentStep = octavePathSelection.length; // 0..pathLength-1
      const baseDegrees = ["R", "2", "3", "4", "5", "6", "7"];
      const expectedDegrees = Array.from({ length: pathLength }, (_, i) => {
        if (i === 0) return "R";
        if (i === pathLength - 1) return "R";
        return baseDegrees[(i - 1) % 7 + 1];
      });
      const expectedDegree = expectedDegrees[currentStep];

      // Buscar si la nota clickeada tiene el grado esperado en el patrón de escala
      const scalePos = scalePattern.positions.find(
        (p) => p.string === stringNum && p.fret === fret
      );
      const isExpectedDegree = scalePos?.degree === expectedDegree;

      // Para el paso 0 (R), debe coincidir con la tónica de inicio de esta sub-misión
      const startTonic = availableOctaveTonics[octaveSubStep];
      if (currentStep === 0) {
        if (!startTonic || stringNum !== startTonic.string || fret !== startTonic.fret) {
          setLearnFeedback("fail");
          setTimeout(() => setLearnFeedback(null), 700);
          return;
        }
      } else if (!isExpectedDegree) {
        setLearnFeedback("fail");
        setTimeout(() => setLearnFeedback(null), 700);
        return;
      }

      // Regla: orden ascendente de pitch
      if (octavePathSelection.length > 0) {
        const lastMidi = getNoteAtPosition(
          octavePathSelection[octavePathSelection.length - 1].string,
          octavePathSelection[octavePathSelection.length - 1].fret
        ).midi;
        if (clickedMidi <= lastMidi) {
          setLearnFeedback("fail");
          setTimeout(() => setLearnFeedback(null), 700);
          return;
        }
      }

      // Evitar duplicados exactos en el camino actual
      if (octavePathSelection.some((p) => p.string === stringNum && p.fret === fret)) {
        setLearnFeedback("fail");
        setTimeout(() => setLearnFeedback(null), 700);
        return;
      }

      const clickedPos: NeckPosition = {
        string: stringNum,
        fret,
        note: clickedNote,
        degree: expectedDegree,
        highlight: expectedDegree === "R" ? "root" : "scale",
      };

      const newSelection = [...octavePathSelection, clickedPos];
      setOctavePathSelection(newSelection);
      setLearnFeedback("success");
      setTimeout(() => setLearnFeedback(null), 700);

      if (newSelection.length === pathLength) {
        // Camino completado: guardar el camino real
        const savedPath = [...newSelection];
        const allSaved = [...octaveSavedPathsRef.current, savedPath];
        setOctaveSavedPaths(allSaved);
        octaveSavedPathsRef.current = allSaved;
        const hash = newSelection.map((p) => `${p.string}-${p.fret}`).join("|");
        setOctavePathsDone((prev) => {
          const next = new Set(prev);
          next.add(hash);
          return next;
        });

        // Mostrar modal de camino completado
        const nextTonic = availableOctaveTonics[octaveSubStep + 1];
        if (octaveSubStep < 2) {
          setOctavePathCompleteProps({
            title: `¡Camino ${octaveSubStep + 1} completado!`,
            message: `Encontraste las ${pathLength} notas de la escala desde ${rootNote}. Ahora busca la siguiente tónica${nextTonic ? ` en la cuerda ${nextTonic.string}` : ""} para construir el siguiente camino.`,
            nextLabel: "Siguiente camino",
            isFinal: false,
          });
        } else {
          setOctavePathCompleteProps({
            title: "¡Todos los caminos completados!",
            message: `Encontraste 3 caminos de la escala de ${rootNote}. Ahora deberás tocar las notas que encontraste en tu guitarra. El micrófono validará cada una.`,
            nextLabel: "Comenzar a tocar",
            isFinal: true,
          });
        }
        setTimeout(() => setShowOctavePathComplete(true), 600);
      }
      return;
    }

    // Modo triada libre del explorador (sin misión)
    if (focusLevel === 2) {
      const clickedPos = scalePattern.positions.find(
        (p) => p.string === stringNum && p.fret === fret && p.fret <= maxDisplayFret
      );
      if (
        clickedPos &&
        (clickedPos.degree === "R" || clickedPos.degree === "3" || clickedPos.degree === "b3" || clickedPos.degree === "5")
      ) {
        setCurrentTriadSelection((prev) => {
          // Evitar duplicados exactos
          if (prev.some((p) => p.string === stringNum && p.fret === fret)) return prev;
          // Evitar repetir la misma clase de nota (mismo pitch class)
          const clickedSemitone = noteToSemitones(clickedPos.note);
          if (prev.some((p) => noteToSemitones(p.note) === clickedSemitone)) return prev;

          const newSelection = [...prev, clickedPos];

          // Si tenemos 3 notas distintas, confirmar grupo
          if (newSelection.length === 3) {
            const hasRoot = newSelection.some((p) => p.degree === "R");
            const hasThird = newSelection.some((p) => p.degree === "3" || p.degree === "b3");
            const hasFifth = newSelection.some((p) => p.degree === "5");
            if (hasRoot && hasThird && hasFifth) {
              setTriadGroups((groups) => [
                ...groups,
                { positions: newSelection, number: groups.length + 1 },
              ]);
              return []; // limpiar selección actual
            }
          }

          return newSelection;
        });
      }
    }
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Control") setIsBuildingCustomPath(true);
  }, []);

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    if (e.key === "Control") setIsBuildingCustomPath(false);
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  // Iniciar micrófono y primera nota al pasar a la misión 4 de pentatónica (subStep 3)
  useEffect(() => {
    if (pentatonicMissionMode && pentatonicSubStep === 3) {
      startListening().catch(console.error);
      // Mostrar la primera nota a tocar
      if (pentatonicPlayTarget.length > 0) {
        const first = pentatonicPlayTarget[0];
        setActiveNote({ string: first.string, fret: first.fret });
      }
    }
  }, [pentatonicMissionMode, pentatonicSubStep, startListening, pentatonicPlayTarget]);

  // Iniciar micrófono y primera nota al pasar a la misión 4 de octava (subStep 3)
  useEffect(() => {
    if (octaveMissionMode && octaveSubStep === 3) {
      startListening().catch(console.error);
      // Mostrar la primera nota a tocar
      if (octavePlayTarget.length > 0) {
        const first = octavePlayTarget[0];
        setActiveNote({ string: first.string, fret: first.fret });
      }
    }
  }, [octaveMissionMode, octaveSubStep, startListening, octavePlayTarget]);

  // Limpiar selección de triadas al cambiar escala o nivel
  useEffect(() => {
    setCurrentTriadSelection([]);
    setTriadGroups([]);
    setSelectedPatterns([]);
    // Salir de todos los modos de aprendizaje
    setTonicDiscoveryMode(false);
    setDiscoveredTonics(new Set());
    setLearnPhase("discovering");
    setShowLearnIntro(false);
    setTriadOrderedMode(false);
    setOrderedSelection([]);
    setOrderedTriadsDone(new Set());
    setShowTriadOrderedIntro(false);
    setTriadFreeMissionMode(false);
    setFreeTriadsDone(new Set());
    setShowTriadFreeIntro(false);
    setPentatonicMissionMode(false);
    setPentatonicSubStep(0);
    setPentatonicPathSelection([]);
    setPentatonicPathsDone(new Set());
    setPentatonicSavedPaths([]);
    setPentatonicPlayTarget([]);
    setPentatonicPlayIndex(0);
    setShowPentatonicIntro(false);
    setOctaveMissionMode(false);
    setOctaveSubStep(0);
    setOctavePathSelection([]);
    setOctavePathsDone(new Set());
    setOctaveSavedPaths([]);
    setOctavePlayTarget([]);
    setOctavePlayIndex(0);
    setShowOctaveIntro(false);
    setShowOctavePathComplete(false);
    setShowMissionComplete(false);
    setCurrentMissionStep(0);
  }, [rootNote, scaleType, pedagogicalLevel, focusLevel]);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [showNotePicker, setShowNotePicker] = useState(false);
  const [showScalePicker, setShowScalePicker] = useState(false);

  // ─── Navegación circular de notas ───
  const handlePrevNote = useCallback(() => {
    const idx = NOTES.indexOf(rootNote);
    const prev = idx > 0 ? NOTES[idx - 1] : NOTES[NOTES.length - 1];
    setRootNote(prev);
  }, [rootNote]);

  const handleNextNote = useCallback(() => {
    const idx = NOTES.indexOf(rootNote);
    const next = idx < NOTES.length - 1 ? NOTES[idx + 1] : NOTES[0];
    setRootNote(next);
  }, [rootNote]);

  // ─── Navegación circular de escalas ───
  const handlePrevScale = useCallback(() => {
    const idx = SCALE_TYPES.findIndex((s) => s.value === scaleType);
    const prev = idx > 0 ? SCALE_TYPES[idx - 1] : SCALE_TYPES[SCALE_TYPES.length - 1];
    setScaleType(prev.value);
  }, [scaleType]);

  const handleNextScale = useCallback(() => {
    const idx = SCALE_TYPES.findIndex((s) => s.value === scaleType);
    const next = idx < SCALE_TYPES.length - 1 ? SCALE_TYPES[idx + 1] : SCALE_TYPES[0];
    setScaleType(next.value);
  }, [scaleType]);

  const handleLearnPrimary = useCallback(() => {
    if (
      mode === "learn" &&
      surface === "keyboard" &&
      pedagogicalLevel === 1 &&
      focusLevel >= 1 &&
      focusLevel <= 5
    ) {
      setPianoLearnActive(false);
      setPianoLearnFoundMidis(new Set());
      setLearnPhase("discovering");
      setShowPianoLearnIntro(true);
      return;
    }
    if (pedagogicalLevel === 1 && focusLevel === 1 && !tonicDiscoveryMode) {
      setShowLearnIntro(true);
      return;
    }
    if (pedagogicalLevel === 1 && focusLevel === 2 && !triadOrderedMode && !triadFreeMissionMode) {
      setShowTriadOrderedIntro(true);
      return;
    }
    if (focusLevel === 3 && !pentatonicMissionMode) {
      setShowPentatonicIntro(true);
      return;
    }
    if (focusLevel === 4 && !octaveMissionMode) {
      setShowOctaveIntro(true);
      return;
    }
    setIsPlaying(true);
    setCurrentSeqIndex(0);
  }, [
    mode,
    surface,
    pedagogicalLevel,
    focusLevel,
    tonicDiscoveryMode,
    triadOrderedMode,
    triadFreeMissionMode,
    pentatonicMissionMode,
    octaveMissionMode,
  ]);

  const pianoLearnIntroContent = useMemo(() => {
    if (surface !== "keyboard" || pedagogicalLevel !== 1) return null;
    return getPianoLearnIntro(focusLevel as PianoFocusStep, rootNote, scaleLabel);
  }, [surface, pedagogicalLevel, focusLevel, rootNote, scaleLabel]);

  const pianoStaffEvents = useMemo(() => {
    if (surface !== "keyboard" || pedagogicalLevel !== 1 || focusLevel < 1 || focusLevel > 5) {
      return undefined;
    }
    if (!pianoLearnActive && !showPianoLearnIntro && !showPianoIntervalTransition) {
      return undefined;
    }
    return buildPianoStepStaffEvents(
      rootNote,
      scaleType,
      focusLevel as PianoFocusStep,
      pianoIntervalDirForLearn
    );
  }, [
    surface,
    pedagogicalLevel,
    focusLevel,
    rootNote,
    scaleType,
    pianoLearnActive,
    showPianoLearnIntro,
    showPianoIntervalTransition,
    pianoIntervalDirForLearn,
  ]);

  const handlePianoFocusLevel = useCallback(
    (level: 1 | 2 | 3 | 4 | 5) => {
      if (surface === "keyboard" && pedagogicalLevel === 1 && mode === "learn") {
        pianoKeepIntroRef.current = true;
        setFocusLevel(level);
        setPianoLearnActive(false);
        setPianoLearnIntervalDir("asc");
        setShowPianoIntervalTransition(false);
        setPianoLearnFoundMidis(new Set());
        setLearnPhase("discovering");
        setShowMissionComplete(false);
        setShowPianoLearnIntro(true);
        return;
      }
      setFocusLevel(level);
    },
    [surface, pedagogicalLevel, mode]
  );

  const startPianoLearnExploration = useCallback(() => {
    setShowPianoLearnIntro(false);
    setShowPianoIntervalTransition(false);
    setPianoLearnIntervalDir("asc");
    setPianoLearnActive(true);
    setPianoLearnFoundMidis(new Set());
    setLearnPhase("discovering");
    setIsPlaying(false);
    setCurrentSeqIndex(0);
    setActiveNote(null);
  }, []);

  const startPianoDescExploration = useCallback(() => {
    setShowPianoIntervalTransition(false);
    setPianoLearnIntervalDir("desc");
    setPianoLearnActive(true);
    setPianoLearnFoundMidis(new Set());
    setLearnPhase("discovering");
    setActiveNote(null);
  }, []);

  const pianoIntervalTransitionContent = useMemo(() => {
    if (focusLevel !== 2 && focusLevel !== 3) return null;
    return getPianoIntervalTransitionIntro(
      focusLevel as 2 | 3,
      "desc",
      rootNote,
      scaleLabel
    );
  }, [focusLevel, rootNote, scaleLabel]);

  const learnCtaLabel = useMemo(() => {
    if (pianoLearnActive) return "Explorando…";
    if (tonicDiscoveryMode) return "Descubriendo…";
    if (triadOrderedMode || triadFreeMissionMode) return "Triadas…";
    if (pentatonicMissionMode) return "Camino…";
    if (octaveMissionMode) return "Octava…";
    if (surface === "keyboard" && pedagogicalLevel === 1) return "Aprender";
    if (pedagogicalLevel === 1 && focusLevel <= 2) return "Aprender";
    if (focusLevel === 1) return "Escuchar I";
    if (focusLevel === 2) return "Escuchar III–V";
    return "Aprender";
  }, [
    pianoLearnActive,
    surface,
    tonicDiscoveryMode,
    triadOrderedMode,
    triadFreeMissionMode,
    pentatonicMissionMode,
    octaveMissionMode,
    pedagogicalLevel,
    focusLevel,
  ]);

  const learnBusy =
    pianoLearnActive ||
    tonicDiscoveryMode ||
    triadOrderedMode ||
    triadFreeMissionMode ||
    pentatonicMissionMode ||
    octaveMissionMode;

  // ─── Sidebar de práctica ───
  const practiceSidebar = (
    <div className="space-y-4">
      {/* Header: Escala en práctica */}
      <div className="text-center pb-3 border-b border-white/15">
        <div className="text-[10px] font-sans font-medium uppercase tracking-widest text-slate-500">
          Practicando
        </div>
        <div className="mt-2 w-16 h-16 mx-auto rounded-full bg-[var(--bordona-accent)] text-white flex items-center justify-center text-3xl font-serif font-bold shadow-lg">
          {rootNote}
        </div>
        <div className="mt-2 text-lg font-serif font-bold italic text-slate-100">
          {SCALE_TYPES.find((s) => s.value === scaleType)?.label}
        </div>
        <div className="mt-1 text-xs font-sans font-semibold text-[#5BA8A8]">
          Paso {focusLevel}: {focusLevel === 1 ? "Tónica" : focusLevel === 2 ? "Triada" : focusLevel === 3 ? "Pentatónica" : "Una 8va"}
        </div>
        <div className="mt-2 text-[11px] font-sans text-slate-400">
          {playbackSequence.length} notas · Nivel {pedagogicalLevel}
        </div>
      </div>

      {/* Temporizador + puntaje */}
      <div className="p-3 rounded-xl bg-white/[0.08] border border-white/10 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="text-center flex-1">
            <div className="text-[10px] font-sans text-slate-400 uppercase tracking-wider">Tiempo</div>
            <div className="text-xl font-sans font-bold text-slate-100">
              {Math.floor(practiceElapsed / 60)}:{String(practiceElapsed % 60).padStart(2, "0")}
            </div>
          </div>
          <div className="w-px h-8 bg-white/15" />
          <div className="text-center flex-1">
            <div className="text-[10px] font-sans text-slate-400 uppercase tracking-wider">Precisión</div>
            <div className="text-xl font-sans font-bold text-slate-100">
              {practiceAttempts > 0 ? Math.round((practiceScore / practiceAttempts) * 100) : 0}%
            </div>
          </div>
          <div className="w-px h-8 bg-white/15" />
          <div className="text-center flex-1">
            <div className="text-[10px] font-sans text-slate-400 uppercase tracking-wider">Misiones</div>
            <div className="text-xl font-sans font-bold text-[var(--bordona-accent)]">
              {practiceMissionsCompleted}
            </div>
          </div>
        </div>
      </div>

      {/* Progreso de notas */}
      <div>
        <div className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-400 mb-2">
          Progreso
        </div>
        <div className="flex gap-1">
          {playbackSequence.map((_, idx) => (
            <div
              key={idx}
              className={`flex-1 h-2 rounded-full transition-all ${
                idx < currentSeqIndex ? "bg-[#8FB069]" : idx === currentSeqIndex ? "bg-[#5BA8A8]" : "bg-white/10"
              }`}
            />
          ))}
        </div>
        <div className="mt-1 text-[10px] font-sans text-slate-400">
          Nota {Math.min(currentSeqIndex + 1, playbackSequence.length)} de {playbackSequence.length}
        </div>
      </div>

      {/* Focus actual */}
      <div className="p-3 rounded-xl bg-white/[0.07]">
        <div className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-400 mb-1">
          Focus actual
        </div>
        <div className="text-sm font-sans font-bold text-slate-100">
          {LEVEL_CONFIG[pedagogicalLevel].stepDesc[focusLevel - 1]}
        </div>
      </div>

      {/* Estado */}
      {isPlaying && (
        <div className="flex items-center gap-2 text-[10px] font-sans text-green-600">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
          </span>
          {pitchState.isListening ? "Escuchando…" : "Toca la nota indicada"}
        </div>
      )}

      {!isPlaying && practiceScore > 0 && (
        <div className="p-3 rounded-xl bg-white/[0.07] text-center">
          <div className="text-sm font-sans font-bold text-slate-100">
            ¡{practiceScore}/{playbackSequence.length} notas!
          </div>
          <div className="text-[10px] font-sans text-slate-400">
            Tiempo: {Math.floor(practiceElapsed / 60)}:{String(practiceElapsed % 60).padStart(2, "0")}
          </div>
        </div>
      )}
      {!isPlaying && (
        <button
          type="button"
          onClick={() => setShowPracticeIntro(true)}
          className="w-full h-10 rounded-xl bg-[#5BA8A8] text-white text-sm font-sans font-semibold flex items-center justify-center gap-2 shadow-md hover:bg-[#4A9898] transition-all"
        >
          Comenzar a practicar
        </button>
      )}
      {isPlaying && (
        <button
          type="button"
          onClick={() => {
            setIsPlaying(false);
            setActiveNote(null);
            stopListening();
          }}
          className="w-full h-9 rounded-xl border border-white/15 text-slate-200 text-sm font-sans hover:bg-white/10"
        >
          Detener práctica
        </button>
      )}
      {playbackControls}
    </div>
  );

  // ─── Sidebar narrativo ───
  const sidebar = (
    <div className="space-y-4">
      {/* Header: Selector circular de nota + escala */}
      <div className="text-center pb-3 border-b border-white/15 space-y-3">
        <div className="text-[10px] font-sans font-medium uppercase tracking-widest text-slate-500">
          {isSandbox ? "Explorando" : "Hoy practicamos"}
        </div>

        {/* Selector de nota */}
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={handlePrevNote}
            className="w-8 h-8 rounded-full bg-white/[0.07] flex items-center justify-center text-slate-200 hover:bg-white/15 transition-all"
            title="Nota anterior"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
          </button>
          <button
            onClick={() => setShowNotePicker(!showNotePicker)}
            className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--bordona-accent-hover)] to-[var(--bordona-accent)] text-white flex items-center justify-center text-3xl font-serif font-bold shadow-lg hover:scale-105 transition-transform"
            title="Cambiar tónica"
          >
            {rootNote}
          </button>
          <button
            onClick={handleNextNote}
            className="w-8 h-8 rounded-full bg-white/[0.07] flex items-center justify-center text-slate-200 hover:bg-white/15 transition-all"
            title="Nota siguiente"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
          </button>
        </div>

        {/* Selector de escala */}
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={handlePrevScale}
            className="w-7 h-7 rounded-full bg-white/[0.07] flex items-center justify-center text-slate-200 hover:bg-white/15 transition-all"
            title="Escala anterior"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
          </button>
          <button
            onClick={() => setShowScalePicker(!showScalePicker)}
            className="text-lg font-serif font-bold italic text-slate-100 hover:text-[var(--bordona-accent)] transition-colors px-2"
            title="Cambiar escala"
          >
            {SCALE_TYPES.find((s) => s.value === scaleType)?.label}
          </button>
          <button
            onClick={handleNextScale}
            className="w-7 h-7 rounded-full bg-white/[0.07] flex items-center justify-center text-slate-200 hover:bg-white/15 transition-all"
            title="Escala siguiente"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
          </button>
        </div>

        <div className="text-[11px] font-sans text-slate-400">
          {scalePattern.notes.length} notas · {scalePattern.degrees.length} grados
        </div>

        <button
          onClick={() => {
            const saved = toggleFavorite({
              type: "scale",
              title: `${rootNote} ${SCALE_TYPES.find((s) => s.value === scaleType)?.label}`,
              subtitle: isSandbox ? "Sandbox libre" : `Nivel ${pedagogicalLevel}`,
              url: "/scales/explorer",
            });
            // Toast feedback could be added here
          }}
          className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-sans font-medium transition-all ${
            isFavorite("/scales/explorer")
              ? "bg-[var(--bordona-accent)]/10 text-[var(--bordona-accent)] border border-[var(--bordona-accent)]/20"
              : "bg-white/[0.07] text-slate-400 border border-white/15 hover:bg-white/10 hover:text-slate-200"
          }`}
        >
          <Bookmark className={`w-3 h-3 ${isFavorite("/scales/explorer") ? "fill-current" : ""}`} />
          {isFavorite("/scales/explorer") ? "Guardado" : "Guardar escala"}
        </button>

        {/* Picker de notas */}
        {showNotePicker && (
          <div className="p-2 rounded-xl bg-white/[0.08] border border-white/15" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
            <div className="grid grid-cols-6 gap-1">
              {NOTES.map((n) => (
                <button
                  key={n}
                  onClick={() => { setRootNote(n); setShowNotePicker(false); }}
                  className={`h-8 rounded-md text-sm font-sans font-bold transition-all ${
                    rootNote === n
                      ? "bg-[var(--bordona-accent)] text-white shadow-sm"
                      : "bg-white/[0.07] text-slate-200 hover:bg-white/10"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Picker de escalas */}
        {showScalePicker && (
          <div className="p-2 rounded-xl bg-white/[0.08] border border-white/15" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
            <div className="grid grid-cols-1 gap-1">
              {SCALE_TYPES.map((scale) => (
                <button
                  key={scale.value}
                  onClick={() => { setScaleType(scale.value); setShowScalePicker(false); }}
                  className={`h-8 rounded-md text-sm font-sans font-bold transition-all ${
                    scaleType === scale.value
                      ? "bg-[var(--bordona-accent)] text-white shadow-sm"
                      : "bg-white/[0.07] text-slate-200 hover:bg-white/10"
                  }`}
                >
                  {scale.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {!isSandbox && (
      <>
      {/* Selector de Nivel pedagógico */}
      <div>
        <div className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
          Nivel pedagógico
          <HelpTooltip term="Nivel pedagógico" explanation="Progresion gradual de dificultad. Nivel 1 = primeros 5 trastes. Nivel 2 = hasta el 8vo. Nivel 3 = todo el mástil." />
        </div>
        <div className="flex gap-2">
          {([1, 2, 3] as const).map((lvl) => (
            <button
              key={lvl}
              onClick={() => setPedagogicalLevel(lvl)}
              className={`flex-1 h-9 rounded-xl text-xs font-sans font-semibold transition-all ${
                pedagogicalLevel === lvl
                  ? "bg-[#2C2825] text-white shadow-md"
                  : "bg-white/[0.07] text-slate-400 hover:bg-white/10"
              }`}
            >
              Nivel {lvl}
            </button>
          ))}
        </div>
        <div className="mt-1.5 text-[10px] font-sans text-slate-400">
          {LEVEL_CONFIG[pedagogicalLevel].desc}
        </div>
      </div>

      {/* Paso a paso: Tónica → Triada → Completa */}
      <div>
        <div className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2">
          Paso {focusLevel} de {surface === "keyboard" && pedagogicalLevel === 1 ? 5 : 4}
        </div>
        <div className={`flex gap-2 ${surface === "keyboard" && pedagogicalLevel === 1 ? "flex-wrap" : ""}`}>
          {(surface === "keyboard" && pedagogicalLevel === 1
            ? PIANO_LEVEL1_STEPS
            : [
                { level: 1 as const, label: "Tónica", color: SATURATED_DEGREE_HEX[0], short: "1" },
                { level: 2 as const, label: "Triada", color: "#5BA8A8", short: "2" },
                { level: 3 as const, label: "Pentatónica", color: "#D4A373", short: "3" },
                { level: 4 as const, label: "Una 8va", color: "#8FB069", short: "4" },
              ]
          ).map((f, idx) => {
            const isActive = focusLevel === f.level;
            const isPast = focusLevel > f.level;
            return (
              <button
                key={f.level}
                type="button"
                onClick={() => handlePianoFocusLevel(f.level)}
                className={`flex-1 min-w-[3.5rem] flex flex-col items-center gap-1 py-2 rounded-xl transition-all ${
                  isActive
                    ? "bg-white/12 shadow-md ring-2 ring-white/25"
                    : isPast
                    ? "bg-white/[0.07] opacity-80"
                    : "bg-white/[0.07] opacity-50"
                }`}
                style={isActive ? { outline: `2px solid ${f.color}` } : {}}
                title={
                  surface === "keyboard" && pedagogicalLevel === 1
                    ? pianoStepDescription(f.level as PianoFocusStep)
                    : LEVEL_CONFIG[pedagogicalLevel].stepDesc[idx]
                }
              >
                <span
                  className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    isActive || isPast ? "text-white" : "text-slate-400 bg-white/15"
                  }`}
                  style={isActive || isPast ? { backgroundColor: f.color } : {}}
                >
                  {isPast
                    ? "✓"
                    : surface === "keyboard" && pedagogicalLevel === 1 && "short" in f
                    ? (f as { short: string }).short
                    : idx + 1}
                </span>
                <span className={`text-[10px] font-sans font-semibold ${isActive ? "text-slate-100" : "text-slate-400"}`}>
                  {f.label}
                </span>
              </button>
            );
          })}
        </div>

        {surface === "keyboard" && pedagogicalLevel === 1 && (
          <p className="mt-2 text-[10px] font-sans text-slate-400 leading-relaxed">
            {pianoStepDescription(focusLevel as PianoFocusStep)}
          </p>
        )}

        {surface !== "keyboard" && focusLevel === 2 && (
          <div className="mt-2 px-3 py-2 rounded-lg bg-[#5BA8A8]/10 border border-[#5BA8A8]/20">
            <p className="text-[10px] font-sans leading-relaxed" style={{ color: "#2C2825" }}>
              <span className="font-semibold">Triada:</span> Solo mostramos las 3 notas mas importantes del acorde. Las otras estan "durmiendo" por ahora.
            </p>
          </div>
        )}
        {surface !== "keyboard" && focusLevel === 3 && (
          <div className="mt-2 px-3 py-2 rounded-lg bg-[#D4A373]/10 border border-[#D4A373]/20">
            <p className="text-[10px] font-sans leading-relaxed" style={{ color: "#2C2825" }}>
              <span className="font-semibold">Pentatonica:</span> Eliminamos 2 notas problematicas para que sea mas facil improvisar.
            </p>
          </div>
        )}
      </div>
      </>
      )}

      {/* Acciones principales — misiones guiadas (solo Aprender) */}
      {!isSandbox && (
      <div className="space-y-2">
        <button
          type="button"
          onClick={handleLearnPrimary}
          disabled={learnBusy}
          className="w-full h-10 rounded-xl bg-[var(--bordona-accent)] text-white text-sm font-sans font-semibold flex items-center justify-center gap-2 shadow-md hover:bg-[var(--bordona-accent-hover)] transition-all disabled:opacity-60"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
          {tonicDiscoveryMode
            ? "Descubriendo tónicas…"
            : triadOrderedMode || triadFreeMissionMode
            ? "Descubriendo triadas…"
            : pentatonicMissionMode
            ? "Construyendo camino…"
            : octaveMissionMode
            ? "Construyendo camino…"
            : pedagogicalLevel === 1 && focusLevel === 1
            ? "Comenzar a aprender"
            : pedagogicalLevel === 1 && focusLevel === 2
            ? "Comenzar a aprender"
            : focusLevel === 1
            ? "Escuchar tónicas"
            : focusLevel === 2
            ? "Escuchar triadas"
            : focusLevel === 3
            ? "Comenzar a aprender"
            : "Comenzar a aprender"}
        </button>
        <button
          onClick={() => {
            if (tonicDiscoveryMode || triadOrderedMode || triadFreeMissionMode) return;
            setRouteMode(routeMode === "tonic" ? "off" : "tonic");
          }}
          disabled={tonicDiscoveryMode || triadOrderedMode || triadFreeMissionMode || pentatonicMissionMode || octaveMissionMode}
          className={`w-full h-10 rounded-xl text-sm font-sans font-semibold flex items-center justify-center gap-2 transition-all ${
            tonicDiscoveryMode || triadOrderedMode || triadFreeMissionMode || pentatonicMissionMode || octaveMissionMode
              ? "bg-white/15 text-slate-500 cursor-not-allowed"
              : routeMode === "tonic"
              ? "bg-[#2C2825] text-white shadow-md"
              : "bg-white/[0.07] text-slate-200 hover:bg-white/10"
          }`}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 21h5v-5"/></svg>
          {tonicDiscoveryMode || triadOrderedMode || triadFreeMissionMode || pentatonicMissionMode || octaveMissionMode
            ? "Ruta bloqueada durante aprendizaje"
            : routeMode === "tonic"
            ? "Ocultar ruta"
            : "Ver ruta de la tónica"}
        </button>
      </div>
      )}

      {playbackControls}

      {/* Modo avanzado */}
      <div className="pt-2 border-t border-white/15">
        <button
          onClick={() => setAdvancedOpen(!advancedOpen)}
          className="w-full flex items-center justify-between text-[11px] font-sans font-semibold text-slate-400 hover:text-slate-100 transition-colors py-1"
        >
          <span>Modo avanzado</span>
          <svg
            className={`w-4 h-4 transition-transform duration-300 ${advancedOpen ? "rotate-180" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {advancedOpen && (
          <div className="mt-2 space-y-3 pb-1">
            {/* Punto de inicio */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1 block">
                Inicio
              </label>
              <div className="flex gap-1">
                <button
                  onClick={() => { setStartFromRoot(true); setStartPosition(null); setIsPickingStart(false); }}
                  className={`flex-1 h-8 rounded-md text-xs font-sans font-medium transition-all ${
                    startFromRoot ? "bg-[var(--bordona-accent)] text-white shadow-sm" : "bg-white/[0.07] text-slate-200 hover:bg-white/10"
                  }`}
                >
                  Desde tónica
                </button>
                <button
                  onClick={() => { setIsPickingStart(!isPickingStart); setStartFromRoot(false); }}
                  className={`flex-1 h-8 rounded-md text-xs font-sans font-medium transition-all ${
                    !startFromRoot ? "bg-[#2C2825] text-white shadow-sm" : "bg-white/[0.07] text-slate-200 hover:bg-white/10"
                  }`}
                >
                  {isPickingStart ? "Click mástil" : "Elegir"}
                </button>
              </div>
            </div>

            {/* Patrón */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1 block">
                Patrón
              </label>
              <div className="grid grid-cols-2 gap-1">
                <button
                  onClick={() => { setPattern("linear"); setDirection("asc"); }}
                  className={`text-left px-2 py-1.5 rounded-md text-xs font-sans transition-all ${
                    pattern === "linear" && direction === "asc" ? "bg-[var(--bordona-accent)] text-white shadow-sm" : "text-slate-200 hover:bg-white/10 bg-white/[0.07]"
                  }`}
                >
                  Lineal
                </button>
                <button
                  onClick={() => { setPattern("linear"); setDirection("both"); }}
                  className={`text-left px-2 py-1.5 rounded-md text-xs font-sans transition-all ${
                    pattern === "linear" && direction === "both" ? "bg-[var(--bordona-accent)] text-white shadow-sm" : "text-slate-200 hover:bg-white/10 bg-white/[0.07]"
                  }`}
                >
                  Ida y vuelta
                </button>
                {PATTERNS.filter((p) => p.value !== "linear").map((p) => (
                  <button
                    key={p.value}
                    onClick={() => { setPattern(p.value); setDirection("asc"); }}
                    className={`text-left px-2 py-1.5 rounded-md text-xs font-sans transition-all ${
                      pattern === p.value ? "bg-[var(--bordona-accent)] text-white shadow-sm" : "text-slate-200 hover:bg-white/10 bg-white/[0.07]"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const fretboardInspector = (
    <div className="space-y-3">
      {/* Tarjeta Contexto */}
      <div
        className="p-4 rounded-2xl bg-white/[0.08] border border-white/10"
        style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
      >
        <div className="text-2xl font-serif font-bold italic text-slate-100 leading-tight">
          {rootNote} {SCALE_TYPES.find((s) => s.value === scaleType)?.label}
        </div>
        <div className="mt-1 text-[11px] font-sans text-slate-400">
          {scalePattern.notes.length} notas · {scalePattern.degrees.length} grados
        </div>
      </div>

      {/* Tarjeta Grados — interactiva */}
      <div
        className="p-4 rounded-2xl bg-white/[0.08] border border-white/10"
        style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
      >
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-3 flex items-center gap-1.5">
          Grados — click para resaltar
          <HelpTooltip term="Grados" explanation="Las notas de una escala tienen nombres segun su funcion. R = Tónica (Raiz), 2 = Supertonica, 3 = Mediante, 4 = Subdominante, 5 = Dominante, 6 = Submediante, 7 = Sensitiva." example="En Do Mayor: R=Do, 3=Mi, 5=Sol" />
        </h4>
        <div className="flex flex-wrap gap-2">
          {scalePattern.degrees.map((deg, idx) => {
            const degreeName = idx === 0 ? "Tónica" : idx === 1 ? "2ª" : idx === 2 ? "3ª" : idx === 3 ? "4ª" : idx === 4 ? "5ª" : idx === 5 ? "6ª" : "7ª";
            const degreeColor = SATURATED_DEGREE_HEX[idx % 7];
            return (
              <button
                key={idx}
                onClick={() => {
                  // Buscar primera posición de este grado en el mástil
                  const pos = highlightedPositions.find((p) => p.degree === deg);
                  if (pos) {
                    setActiveNote({ string: pos.string, fret: pos.fret });
                    playFret(pos.string, pos.fret);
                  }
                }}
                className={`flex items-center justify-center rounded-full font-sans font-bold transition-all hover:scale-110 ${
                  idx === 0
                    ? "w-9 h-9 text-sm text-white shadow-sm"
                    : "w-7 h-7 text-[10px] text-slate-100 bg-white/[0.07] hover:bg-white/[0.1] border-2 border-solid"
                }`}
                style={
                  idx === 0
                    ? { backgroundColor: degreeColor }
                    : { borderColor: degreeColor, boxShadow: `0 0 0 1px ${degreeColor}33` }
                }
                title={degreeName}
              >
                {deg}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tarjeta Nota inspeccionada CAGED */}
      {inspectedFret && (
        <div
          className="p-4 rounded-2xl bg-white/[0.08] border border-white/10"
          style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
        >
          {(() => {
            const ctx = getCagedNoteContext(inspectedFret.string, inspectedFret.fret, rootNote, cagedPatterns);
            return (
              <>
                <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-3">
                  Nota en el mástil
                </h4>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[var(--bordona-accent)] text-white flex items-center justify-center text-lg font-serif font-bold">
                    {ctx.note}
                  </div>
                  <div>
                    <div className="text-sm font-sans font-semibold text-slate-100">
                      Cuerda {inspectedFret.string} · Traste {inspectedFret.fret}
                    </div>
                    <div className="text-[10px] text-slate-400 font-sans">
                      {ctx.isTonic ? "Tónica" : ctx.isThird ? "3ª" : ctx.isFifth ? "5ª" : "Escala"}
                    </div>
                  </div>
                </div>
                {ctx.patterns.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-[10px] font-sans font-semibold text-slate-400">
                      Pertenece a:
                    </div>
                    {ctx.patterns.map((p) => (
                      <div key={p.number} className="flex items-center gap-2 text-[11px] font-sans text-slate-200">
                        <span className="w-4 h-4 rounded bg-white/[0.07] flex items-center justify-center text-[9px] font-bold text-slate-400">
                          {p.number}
                        </span>
                        <span>Forma {p.shape}</span>
                        <span className="text-slate-500">·</span>
                        <span className="text-slate-400">{p.degree}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Tarjeta Cajas / Patrones activos */}
      {selectedPatterns.length > 0 && (
        <div
          className="p-4 rounded-2xl bg-white/[0.08] border border-white/10"
          style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
        >
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-3">
            Patrones activos
          </h4>
          <div className="space-y-2">
            {selectedPatterns.map((fp, idx) => {
              const color = BOX_COLORS[idx % BOX_COLORS.length];
              const isCaged = fp.type === 'caged';
              const def = isCaged ? CAGED_DEFINITIONS.find((d) => `caged-${d.patternNumber}` === fp.id) : null;
              return (
                <div
                  key={fp.id}
                  className="flex items-center gap-2.5 p-2 rounded-xl bg-[#F8F7FA]"
                >
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-sans font-semibold text-slate-100">
                      {isCaged ? `Patrón ${fp.label} (CAGED)` : `Posición ${fp.label}`}
                    </div>
                    <div className="text-[10px] text-slate-400 font-sans">
                      {fp.notes.length} notas · {fp.span} trastes {def ? `· ${def.description}` : ""}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tarjeta Conexiones CAGED */}
      {cagedPatterns.length > 0 && (
        <div
          className="p-4 rounded-2xl bg-white/[0.08] border border-white/10"
          style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
        >
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-3">
            Conexiones entre patrones
          </h4>
          <div className="space-y-2">
            {cagedConnections.map((conn, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-[11px] font-sans text-slate-200"
              >
                <span className="w-5 h-5 rounded-full bg-white/[0.07] flex items-center justify-center text-[9px] font-bold text-slate-400">
                  {conn.fromPattern}
                </span>
                <span className="text-slate-500">→</span>
                <span className="w-5 h-5 rounded-full bg-white/[0.07] flex items-center justify-center text-[9px] font-bold text-slate-400">
                  {conn.toPattern}
                </span>
                <span className="text-slate-400 ml-1">
                  cuerda {conn.sharedString} ({conn.sharedNote})
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tarjeta Info */}
      <div
        className="p-4 rounded-2xl bg-white/[0.08] border border-white/10"
        style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
      >
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-2">
          Info
        </h4>
        <p className="text-[12px] text-slate-200 font-sans leading-relaxed">
          {SCALE_INFO[scaleType]}
        </p>
      </div>
    </div>
  );

  const currentTarget = playbackSequence.length > 0 && currentSeqIndex < playbackSequence.length ? playbackSequence[currentSeqIndex] : null;

  const totalTonics = allTonicPositions.length;
  const foundTonics = discoveredTonics.size;

  const getOrderedStepLabel = () => {
    const step = orderedSelection.length;
    if (step === 0) return "Busca la Fundamental (I)";
    if (step === 1) return "Busca la 3ra (III)";
    return "Busca la 5ta (V)";
  };

  const getOrderedHint = () => {
    const step = orderedSelection.length;
    if (step === 0) return "La fundamental es la nota que da nombre al acorde";
    if (step === 1) return `La 3ra es la nota ${scalePattern.notes[2] || "3ª"}`;
    return `La 5ta es la nota ${scalePattern.notes[4] || "5ª"}`;
  };

  const learnPill = (
    <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20">
      <div
        className={`flex items-center gap-5 px-7 py-4 rounded-2xl bg-black/40 backdrop-blur-xl border border-white/12 shadow-xl transition-colors duration-300 min-w-[320px] ${
          learnFeedback === "success"
            ? "border-[#8FB069]/50"
            : learnFeedback === "fail"
            ? "border-[#E8486E]/50"
            : learnPhase === "completed"
            ? "border-[#D4A373]/50"
            : "border-[#5BA8A8]/30"
        }`}
      >
        {/* Icono de estado */}
        <div className="flex-shrink-0">
          {learnPhase === "completed" ? (
            <div className="w-12 h-12 rounded-full bg-[#D4A373] flex items-center justify-center text-white shadow-md">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
          ) : learnFeedback === "success" ? (
            <div className="w-12 h-12 rounded-full bg-[#8FB069] flex items-center justify-center text-white shadow-md">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
          ) : learnFeedback === "fail" ? (
            <div className="w-12 h-12 rounded-full bg-[#E8486E] flex items-center justify-center text-white shadow-md">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </div>
          ) : (
            <div className="w-12 h-12 rounded-full bg-[#5BA8A8]/20 flex items-center justify-center text-[#5BA8A8] shadow-sm">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            </div>
          )}
        </div>

        {/* Texto principal */}
        <div className="flex-1 min-w-0">
          {learnPhase === "completed" ? (
            <div>
              <div className="text-lg font-serif font-bold text-[#D4A373]">¡Descubrimiento completo!</div>
              <div className="text-xs font-sans text-slate-400 mt-0.5">
                Encontraste todas las {rootNote} en el mástil
              </div>
            </div>
          ) : learnFeedback === "success" ? (
            <div>
              <div className="text-lg font-serif font-bold text-[#8FB069]">
                {triadOrderedMode ? "¡Correcto!" : pentatonicMissionMode ? "¡Nota encontrada!" : octaveMissionMode ? "¡Nota encontrada!" : `¡${rootNote} encontrada!`}
              </div>
              <div className="text-xs font-sans text-slate-400 mt-0.5">
                {triadOrderedMode
                  ? getOrderedStepLabel()
                  : pentatonicMissionMode
                  ? `Paso ${pentatonicPathSelection.length}/5 del camino ${pentatonicSubStep + 1}`
                  : octaveMissionMode
                  ? `Paso ${octavePathSelection.length}/${pedagogicalLevel === 1 ? 8 : pedagogicalLevel === 2 ? 15 : 8} del camino ${octaveSubStep + 1}`
                  : "Sigue buscando más tónicas en el mástil"}
              </div>
            </div>
          ) : learnFeedback === "fail" ? (
            <div>
              <div className="text-lg font-serif font-bold text-[#E8486E]">
                {triadOrderedMode ? "Esa nota no corresponde" : pentatonicMissionMode ? "Esa nota no es la esperada" : octaveMissionMode ? "Esa nota no es la esperada" : "Esa no es la tónica"}
              </div>
              <div className="text-xs font-sans text-slate-400 mt-0.5">
                {triadOrderedMode
                  ? getOrderedHint()
                  : pentatonicMissionMode
                  ? `Busca el grado ${["R", "2", "3", "5", "6"][pentatonicPathSelection.length] || "—"}`
                  : octaveMissionMode
                  ? `Busca el grado ${["R", "2", "3", "4", "5", "6", "7", "R"][octavePathSelection.length] || "—"}`
                  : `Busca la nota ${rootNote} en el mástil`}
              </div>
            </div>
          ) : (
            <div>
              <div className="text-lg font-serif font-bold text-slate-100 leading-tight">
                {triadOrderedMode ? (
                  <span>{getOrderedStepLabel()}</span>
                ) : triadFreeMissionMode ? (
                  <span>Agrupa las triadas de <span className="text-[#5BA8A8]">{rootNote}</span></span>
                ) : pentatonicMissionMode ? (
                  pentatonicSubStep === 3 ? (
                    <span>Toca <span className="text-[var(--bordona-accent)]">{pentatonicPlayTarget[pentatonicPlayIndex]?.note || "—"}</span></span>
                  ) : (
                    <span>Construye el camino <span className="text-[#5BA8A8]">{pentatonicSubStep + 1}</span></span>
                  )
                ) : octaveMissionMode ? (
                  octaveSubStep === 3 ? (
                    <span>Toca <span className="text-[var(--bordona-accent)]">{octavePlayTarget[octavePlayIndex]?.note || "—"}</span></span>
                  ) : (
                    <span>Construye el camino <span className="text-[#5BA8A8]">{octaveSubStep + 1}</span></span>
                  )
                ) : (
                  <span>Busca la <span className="text-[#5BA8A8]">{rootNote}</span></span>
                )}
              </div>
              <div className="text-xs font-sans text-slate-400 mt-0.5">
                {triadOrderedMode
                  ? getOrderedHint()
                  : triadFreeMissionMode
                  ? "Selecciona 3 notas del acorde (I, III, V) en cualquier orden"
                  : pentatonicMissionMode
                  ? pentatonicSubStep === 3
                    ? `Nota ${pentatonicPlayIndex + 1} de ${pentatonicPlayTarget.length} · Cuerda ${pentatonicPlayTarget[pentatonicPlayIndex]?.string || "—"} · Traste ${pentatonicPlayTarget[pentatonicPlayIndex]?.fret || "—"}`
                    : `Haz clic en ${["la tónica", "la 2ª", "la 3ª", "la 5ª", "la 6ª"][pentatonicPathSelection.length] || "la siguiente nota"}`
                  : octaveMissionMode
                  ? octaveSubStep === 3
                    ? `Nota ${octavePlayIndex + 1} de ${octavePlayTarget.length} · Cuerda ${octavePlayTarget[octavePlayIndex]?.string || "—"} · Traste ${octavePlayTarget[octavePlayIndex]?.fret || "—"}`
                    : `Haz clic en ${["la tónica", "la 2ª", "la 3ª", "la 4ª", "la 5ª", "la 6ª", "la 7ª", "la octava"][octavePathSelection.length] || "la siguiente nota"}`
                  : `Haz clic en todas las ${rootNote} que encuentres`}
              </div>
            </div>
          )}
        </div>

        {/* Progreso */}
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className="text-xs font-sans font-bold text-slate-200 tabular-nums">
            {triadOrderedMode
              ? `${orderedTriadsDone.size}/${orderedTriadTargetCount}`
              : triadFreeMissionMode
              ? `${freeTriadsDone.size}/${freeTriadTargetCount}`
              : pentatonicMissionMode
              ? pentatonicSubStep === 3
                ? `${pentatonicPlayIndex + 1}/${pentatonicPlayTarget.length}`
                : `${pentatonicSavedPaths.length}/3 caminos`
              : octaveMissionMode
              ? octaveSubStep === 3
                ? `${octavePlayIndex + 1}/${octavePlayTarget.length}`
                : `${octaveSavedPaths.length}/3 caminos`
              : `${foundTonics}/${totalTonics}`}
          </span>
          <div className="w-20 h-2 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full bg-[#5BA8A8] transition-all duration-300"
              style={{
                width: triadOrderedMode
                  ? `${(orderedTriadsDone.size / orderedTriadTargetCount) * 100}%`
                  : triadFreeMissionMode
                  ? `${(freeTriadsDone.size / freeTriadTargetCount) * 100}%`
                  : pentatonicMissionMode
                  ? pentatonicSubStep === 3
                    ? `${pentatonicPlayTarget.length > 0 ? (pentatonicPlayIndex / pentatonicPlayTarget.length) * 100 : 0}%`
                    : `${(pentatonicSavedPaths.length / 3) * 100}%`
                  : octaveMissionMode
                  ? octaveSubStep === 3
                    ? `${octavePlayTarget.length > 0 ? (octavePlayIndex / octavePlayTarget.length) * 100 : 0}%`
                    : `${(octaveSavedPaths.length / 3) * 100}%`
                  : `${totalTonics > 0 ? (foundTonics / totalTonics) * 100 : 0}%`,
              }}
            />
          </div>
        </div>

        {/* Stop / Cerrar */}
        <button
          onClick={() => {
            setTonicDiscoveryMode(false);
            setTriadOrderedMode(false);
            setTriadFreeMissionMode(false);
            setPentatonicMissionMode(false);
            setOctaveMissionMode(false);
            setDiscoveredTonics(new Set());
            setOrderedTriadsDone(new Set());
            setFreeTriadsDone(new Set());
            setPentatonicPathsDone(new Set());
            setPentatonicSavedPaths([]);
            setPentatonicPathSelection([]);
            setOctavePathsDone(new Set());
            setOctaveSavedPaths([]);
            setOctavePathSelection([]);
            setOrderedSelection([]);
            setCurrentTriadSelection([]);
            setLearnPhase("discovering");
            setIsPlaying(false);
          }}
          className="w-9 h-9 rounded-full bg-[#2C2825] text-white flex items-center justify-center hover:scale-105 transition-all shadow-md flex-shrink-0"
          title="Terminar aprendizaje"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
        </button>
      </div>
    </div>
  );

  const practicePill = (
    <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20">
      <div
        className={`flex items-center gap-5 px-7 py-4 rounded-2xl bg-black/40 backdrop-blur-xl border border-white/12 shadow-xl transition-colors duration-300 min-w-[320px] ${
          practicePhase === "success"
            ? "border-[#8FB069]/50"
            : practicePhase === "fail"
            ? "border-[#E8486E]/50"
            : practicePhase === "silence"
            ? "border-[#D4A373]/50"
            : "border-[#5BA8A8]/30"
        }`}
      >
        {/* Icono de estado */}
        <div className="flex-shrink-0">
          {practicePhase === "success" ? (
            <div className="w-12 h-12 rounded-full bg-[#8FB069] flex items-center justify-center text-white shadow-md">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
          ) : practicePhase === "fail" ? (
            <div className="w-12 h-12 rounded-full bg-[#E8486E] flex items-center justify-center text-white shadow-md">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </div>
          ) : practicePhase === "silence" ? (
            <div className="w-12 h-12 rounded-full bg-[#D4A373] flex items-center justify-center text-white shadow-md">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
            </div>
          ) : practicePhase === "countdown" ? (
            <div className="w-12 h-12 rounded-full bg-[#5BA8A8]/20 flex items-center justify-center text-[#5BA8A8] shadow-sm">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </div>
          ) : (
            <div className="relative flex h-12 w-12">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#5BA8A8] opacity-40" />
              <span className="relative inline-flex rounded-full h-12 w-12 bg-[#5BA8A8] items-center justify-center text-white shadow-md">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
              </span>
            </div>
          )}
        </div>

        {/* Texto principal */}
        <div className="flex-1 min-w-0">
          {practicePhase === "countdown" ? (
            <div className="text-base font-sans font-semibold text-[#5BA8A8]">Prepárate…</div>
          ) : practicePhase === "listen" ? (
            <div>
              <div className="text-lg font-serif font-bold text-slate-100 leading-tight">
                Toca <span className="text-[var(--bordona-accent)]">{currentTarget?.note || "—"}</span>
              </div>
              <div className="text-xs font-sans text-slate-400 mt-0.5">
                {currentTarget ? `Cuerda ${currentTarget.string} · Traste ${currentTarget.fret}` : "—"}
              </div>
            </div>
          ) : practicePhase === "success" ? (
            <div className="text-lg font-serif font-bold text-[#8FB069]">¡Excelente!</div>
          ) : practicePhase === "fail" ? (
            <div>
              <div className="text-lg font-serif font-bold text-[#E8486E]">Inténtalo de nuevo</div>
              <div className="text-xs font-sans text-slate-400 mt-0.5">
                Esperaba {currentTarget?.note || "—"} · Cuerda {currentTarget?.string || "—"} · Traste {currentTarget?.fret || "—"}
              </div>
            </div>
          ) : (
            <div className="text-lg font-serif font-bold text-[#D4A373]">No escuché nada</div>
          )}
        </div>

        {/* Progreso */}
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className="text-xs font-sans font-bold text-slate-200 tabular-nums">
            {Math.min(currentSeqIndex + 1, playbackSequence.length)}/{playbackSequence.length}
          </span>
          <div className="w-20 h-2 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full bg-[#5BA8A8] transition-all duration-300"
              style={{
                width: `${playbackSequence.length > 0 ? ((currentSeqIndex + 1) / playbackSequence.length) * 100 : 0}%`,
              }}
            />
          </div>
        </div>

        {/* Stop */}
        <button
          onClick={() => {
            setIsPlaying(false);
            setActiveNote(null);
            stopListening();
          }}
          className="w-9 h-9 rounded-full bg-[#2C2825] text-white flex items-center justify-center hover:scale-105 transition-all shadow-md flex-shrink-0"
          title="Detener práctica"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
        </button>
      </div>
    </div>
  );

  const activeSidebar = isSandbox
    ? undefined
    : mode === "practice"
    ? practiceSidebar
    : sidebar;

  const handleSandboxDegreeClick = useCallback(
    (degree: string) => {
      if (surface === "keyboard") {
        handlePianoDegreeSelect(degree);
        return;
      }
      const pos = highlightedPositions.find((p) => p.degree === degree);
      if (pos) {
        setActiveNote({ string: pos.string, fret: pos.fret });
        playFret(pos.string, pos.fret);
      }
    },
    [surface, handlePianoDegreeSelect, highlightedPositions, playFret]
  );

  const handleSandboxPlay = useCallback(() => {
    if (playbackSequence.length === 0) return;
    setIsPlaying(true);
    setCurrentSeqIndex(0);
    const first = playbackSequence[0];
    setActiveNote({ string: first.string, fret: first.fret });
    playFret(first.string, first.fret);
  }, [playbackSequence, playFret]);

  const handleSandboxStop = useCallback(() => {
    setIsPlaying(false);
    setActiveNote(null);
    stopListening();
  }, [stopListening]);

  const handleSandboxNext = useCallback(() => {
    if (playbackSequence.length === 0) return;
    const next = Math.min(currentSeqIndex + 1, playbackSequence.length - 1);
    setCurrentSeqIndex(next);
    const pos = playbackSequence[next];
    setActiveNote({ string: pos.string, fret: pos.fret });
    playFret(pos.string, pos.fret);
  }, [playbackSequence, currentSeqIndex, playFret]);

  const sandboxBottom = isSandbox ? (
    <ExploreSandboxTransport
      isPlaying={isPlaying}
      onPlay={handleSandboxPlay}
      onStop={handleSandboxStop}
      onNext={handleSandboxNext}
      currentIndex={currentSeqIndex}
      sequenceLength={playbackSequence.length}
      bpm={bpm}
      onBpmChange={setBpm}
      degrees={scaleDegreeChips}
      onDegreeClick={handleSandboxDegreeClick}
      playbackRange={sandboxPlaybackRange}
      onPlaybackRangeChange={setSandboxPlaybackRange}
    />
  ) : undefined;

  const horizontalDock = isSandbox ? undefined : useBottomDock ? (
    <ScaleHorizontalDock
      sandbox={false}
      mode={mode === "practice" ? "practice" : "learn"}
      rootNote={rootNote}
      scaleLabel={scaleLabel}
      scaleType={scaleType}
      notes={NOTES}
      scaleOptions={SCALE_TYPES}
      onPrevNote={handlePrevNote}
      onNextNote={handleNextNote}
      onPrevScale={handlePrevScale}
      onNextScale={handleNextScale}
      onSelectNote={setRootNote}
      onSelectScale={setScaleType}
      pedagogicalLevel={pedagogicalLevel}
      onPedagogicalLevel={setPedagogicalLevel}
      focusLevel={focusLevel}
      onFocusLevel={handlePianoFocusLevel}
      focusSteps={
        surface === "keyboard" && pedagogicalLevel === 1 ? PIANO_LEVEL1_STEPS : undefined
      }
      routeMode={routeMode}
      onRouteMode={setRouteMode}
      routeLocked={routeLocked}
      octaves={octaves}
      onOctaves={setOctaves}
      direction={direction}
      onDirection={setDirection}
      pattern={pattern}
      bpm={bpm}
      onBpm={setBpm}
      learnCtaLabel={learnCtaLabel}
      onLearnPrimary={handleLearnPrimary}
      learnBusy={learnBusy}
      isPlaying={isPlaying}
      onStartPractice={() => setShowPracticeIntro(true)}
      onStopPractice={() => {
        setIsPlaying(false);
        setActiveNote(null);
        stopListening();
      }}
      practiceElapsed={practiceElapsed}
      practiceScore={practiceScore}
      practiceAttempts={practiceAttempts}
      currentSeqIndex={currentSeqIndex}
      sequenceLength={playbackSequence.length}
      isListening={pitchState.isListening}
    />
  ) : undefined;

  return (
    <SongLearnCanvasBackdrop className="min-h-[100dvh] h-[100dvh] overflow-hidden">
    <WorkbenchLayout
      immersive={isSandbox}
      hideLeftSidebar={isSandbox || useBottomDock}
      sidebar={activeSidebar}
      bottom={isSandbox ? sandboxBottom : horizontalDock}
      bottomDock={isSandbox || useBottomDock}
      inspector={
        isSandbox
          ? undefined
          : surface === "keyboard" ? (
          <ScalePianoInspector
            rootNote={rootNote}
            scaleTitle={scaleLabel}
            scaleInfo={SCALE_INFO[scaleType]}
            degreeChips={scaleDegreeChips}
            focusedDegree={focusedDegree}
            selectedNoteName={selectedScaleNoteName}
            onDegreeSelect={handlePianoDegreeSelect}
            fingering={pianoFingering}
          />
        ) : (
          fretboardInspector
        )
      }
      title={isSandbox ? undefined : `${rootNote} ${SCALE_TYPES.find((s) => s.value === scaleType)?.label}`}
      activeModule="scales"
      surface="learnCanvas"
    >
      {isSandbox ? (
        <ExploreImmersiveCanvas
          rootNote={rootNote}
          scaleLabel={scaleLabel}
          scaleType={scaleType}
          notes={NOTES}
          scaleOptions={SCALE_TYPES}
          noteCount={scalePattern.notes.length}
          degreeCount={scalePattern.degrees.length}
          onPrevNote={handlePrevNote}
          onNextNote={handleNextNote}
          onPrevScale={handlePrevScale}
          onNextScale={handleNextScale}
          onSelectNote={setRootNote}
          onSelectScale={setScaleType}
          octaves={octaves}
          direction={direction}
          pattern={pattern}
          activeNote={activeNote}
          playbackSequence={playbackSequence}
          currentSeqIndex={currentSeqIndex}
          highlightedPositions={highlightedPositions}
          onFretClick={handleFretClick}
          surface={surface}
          stringOrder={stringOrder}
          leftHanded={leftHanded}
          onStringOrderToggle={() => setStringOrder(stringOrder === "physical" ? "tab" : "physical")}
          onLeftHandedToggle={() => setLeftHanded(!leftHanded)}
          connections={connections}
          dimmedKeys={dimmedKeys}
          focusLevel={focusLevel}
          practiceFeedback={
            (tonicDiscoveryMode || pianoLearnActive ? learnFeedback : practiceFeedback) ?? undefined
          }
          successPositions={successPositions}
          groupedPositions={groupedPositions}
          showFingerBadges={isPlaying}
          mnemonicTip={mnemonicTip}
          npsPatterns={sandboxNpsPatterns}
          npsSelection={sandboxNpsSelection}
          onNpsSelectionChange={setSandboxNpsSelection}
          spotlightKeys={sandboxSpotlightKeys}
          staffHighlightNote={staffHighlightNote}
          hoverNote={staffHoverNote}
          isPlaying={isPlaying}
          onFretHover={(string, fret) => setStaffHoverNote({ string, fret })}
          onFretLeave={() => setStaffHoverNote(null)}
        />
      ) : (
      <div className="flex-1 flex flex-col min-h-0 min-w-0 w-full max-w-full overflow-hidden p-2">
        <div
          className={`w-full h-full relative flex flex-col min-h-0 min-w-0 ${
            useBottomDock ? "" : "max-w-6xl mx-auto"
          }`}
        >
          {/* Pentagrama de la escala */}
          <div
            className={
              surface === "keyboard"
                ? "flex-[1.2] flex flex-col justify-center shrink-0 mb-1"
                : "shrink-0 mb-2"
            }
            style={
              surface === "keyboard" ? { minHeight: PRACTICE_STAFF_MIN_SCALE_KEYBOARD } : undefined
            }
          >
            <PracticeStaff
              rootNote={rootNote}
              scaleType={scaleType}
              activeNote={activeNote}
              trebleNotesOverride={pianoStaffEvents}
              selectedDegree={surface === "keyboard" ? selectedDegree : null}
              onNoteSelect={surface === "keyboard" ? handleStaffNoteSelect : undefined}
              filterMode={
                tonicDiscoveryMode
                  ? "tonic"
                  : triadOrderedMode || triadFreeMissionMode
                  ? "triad"
                  : pentatonicMissionMode
                  ? "pentatonic"
                  : octaveMissionMode
                  ? "octave"
                  : "all"
              }
              playbackSequence={playbackSequence}
              currentSeqIndex={currentSeqIndex}
              learnSurface
            />
            {surface === "keyboard" && pedagogicalLevel === 1 && pianoLearnActive && (
              <p className="text-[10px] text-slate-400 font-sans mt-1 text-center px-2">
                {pianoStepDescription(
                  focusLevel as PianoFocusStep,
                  focusLevel === 2 || focusLevel === 3 ? pianoLearnIntervalDir : undefined
                )}
              </p>
            )}
          </div>

          {/* Superficie de práctica */}
          <div
            className={`flex-1 relative min-h-0 ${surface === "keyboard" ? "flex flex-col justify-end" : ""}`}
          >
            {/* Ayuda mnemotécnica flotante */}
            {mnemonicTip && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 px-4 py-2 rounded-xl bg-[#2C2825] text-white shadow-lg animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="flex items-center gap-2 text-xs font-sans font-semibold">
                  <span className="text-lg">{mnemonicTip.arrow}</span>
                  <span>
                    {mnemonicTip.label}: {mnemonicTip.fromString} (tr. {mnemonicTip.fromFret}) {mnemonicTip.arrow} {mnemonicTip.toString} (tr. {mnemonicTip.toFret})
                  </span>
                </div>
              </div>
            )}
            <PracticeSurface
              highlightedPositions={highlightedPositions}
              activeNote={activeNote}
              onFretClick={handleFretClick}
              showNoteNames={true}
              showDegrees={surface === "keyboard"}
              showMappedKeyLabels={surface === "keyboard"}
              interactive={true}
              stringOrder={stringOrder}
              showFingerBadges={isPlaying}
              hand="left"
              leftHanded={leftHanded}
              connections={connections}
              dimmedKeys={dimmedKeys}
              focusLevel={focusLevel}
              practiceFeedback={
                (tonicDiscoveryMode || pianoLearnActive ? learnFeedback : practiceFeedback) ?? undefined
              }
              successPositions={successPositions}
              groupedPositions={groupedPositions}
              visualStyle="lutherie"
            />

            {/* Orientación flotante (mástil) */}
            {surface === "fretboard" && (
            <div className="absolute top-2 right-2 flex flex-col gap-1.5">
              <button
                onClick={() => setStringOrder(stringOrder === "physical" ? "tab" : "physical")}
                className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-sans font-medium transition-all bg-black/35 backdrop-blur-md border border-white/12 text-slate-100 hover:bg-white/10 shadow-sm"
                title={stringOrder === "physical" ? "Orden físico (grave arriba)" : "Orden tab (agudo arriba)"}
              >
                {stringOrder === "physical" ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="6" y1="3" x2="6" y2="21"/><line x1="12" y1="3" x2="12" y2="21"/><line x1="18" y1="3" x2="18" y2="21"/></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="3" y1="14" x2="21" y2="14"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                )}
              </button>
              <button
                onClick={() => setLeftHanded(!leftHanded)}
                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-sans font-medium transition-all border shadow-sm ${
                  leftHanded
                    ? "bg-[var(--bordona-accent)] text-white border-[var(--bordona-accent)]"
                    : "bg-black/35 backdrop-blur-md border-white/12 text-slate-100 hover:bg-white/10"
                }`}
                title={leftHanded ? "Zurdo" : "Diestro"}
              >
                {leftHanded ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
                )}
              </button>
            </div>
            )}

            {/* FAB dibujar camino (mástil) */}
            {surface === "fretboard" && (
            <>
            <button
              onClick={() => setIsBuildingCustomPath((prev) => !prev)}
              className={`absolute bottom-4 right-4 w-11 h-11 rounded-full flex items-center justify-center shadow-lg transition-all z-10 ${
                isBuildingCustomPath || customSequence.length > 0
                  ? "bg-[#D946EF] text-white scale-110"
                  : "bg-white/12 text-slate-100 hover:bg-white/20 border border-white/15"
              }`}
              title={isBuildingCustomPath ? "Terminar camino" : "Dibujar camino personalizado (Ctrl+click)"}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
            </button>
            {isBuildingCustomPath && (
              <div className="absolute bottom-16 right-4 px-3 py-1.5 rounded-lg bg-[#2C2825] text-white text-[10px] font-sans shadow-lg">
                Haz click en el mástil para agregar notas
              </div>
            )}
            {customSequence.length > 0 && !isBuildingCustomPath && (
              <button
                onClick={() => { setCustomSequence([]); setIsBuildingCustomPath(false); }}
                className="absolute bottom-4 right-16 w-9 h-9 rounded-full flex items-center justify-center bg-white/10 border border-white/15 text-slate-200 hover:bg-red-500/20 hover:text-red-200 shadow-md transition-all z-10"
                title="Limpiar camino"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              </button>
            )}
            </>
            )}

            {tonicDiscoveryMode || triadOrderedMode || triadFreeMissionMode || pentatonicMissionMode ? (
              learnPill
            ) : mode === "practice" && isPlaying ? (
              practicePill
            ) : null}

            {isPickingStart && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-[var(--bordona-accent)] text-white text-xs font-sans font-semibold shadow-lg animate-pulse">
                Haz click en el mástil para elegir el punto de inicio
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      <PracticeIntroModal
        isOpen={showPracticeIntro}
        onStart={handleStartPractice}
        onClose={() => { setShowPracticeIntro(false); setIsPlaying(false); }}
        title={`${rootNote} ${SCALE_TYPES.find((s) => s.value === scaleType)?.label}`}
        missionText={`Paso ${focusLevel}: ${focusLevel === 1 ? "Tónica" : focusLevel === 2 ? "Triada" : focusLevel === 3 ? "Pentatónica" : "Una 8va"} — ${playbackSequence.length} notas`}
      />

      <PracticeIntroModal
        isOpen={showPianoLearnIntro && surface === "keyboard" && pedagogicalLevel === 1}
        onStart={startPianoLearnExploration}
        onClose={() => setShowPianoLearnIntro(false)}
        dismissOnBackdrop={false}
        variant="learn"
        title={pianoLearnIntroContent?.title ?? "Exploración en piano"}
        missionText={
          pianoLearnIntroContent?.missionText ??
          pianoStepDescription(focusLevel as PianoFocusStep)
        }
        steps={
          pianoLearnIntroContent?.steps ?? [
            "Mira el pentagrama y localiza la nota resaltada",
            "Encuentra el Do central en el teclado",
            "Busca la tónica en las teclas coloreadas",
            "Haz clic para comprobar cada nota",
          ]
        }
        footerTip={pianoLearnIntroContent?.footerTip}
        buttonText={pianoLearnIntroContent?.buttonText ?? "Empezar exploración"}
        secondaryButton={{
          text: "Continuar más tarde",
          onClick: () => setShowPianoLearnIntro(false),
        }}
      />

      {pianoIntervalTransitionContent && (
        <PracticeIntroModal
          isOpen={showPianoIntervalTransition}
          onStart={startPianoDescExploration}
          onClose={() => setShowPianoIntervalTransition(false)}
          dismissOnBackdrop={false}
          variant="learn"
          title={pianoIntervalTransitionContent.title}
          missionText={pianoIntervalTransitionContent.missionText}
          steps={pianoIntervalTransitionContent.steps}
          footerTip={pianoIntervalTransitionContent.footerTip}
          buttonText={pianoIntervalTransitionContent.buttonText}
        />
      )}

      <PracticeIntroModal
        isOpen={showLearnIntro}
        onStart={() => {
          setShowLearnIntro(false);
          setTonicDiscoveryMode(true);
          setDiscoveredTonics(new Set());
          setLearnPhase("discovering");
          setIsPlaying(false);
        }}
        onClose={() => { setShowLearnIntro(false); }}
        variant="learn"
        title={`Descubre la tónica de ${rootNote}`}
        missionText={`La tónica (o fundamental) es la nota que le da nombre a la escala. En este caso es ${rootNote}. Tu misión: encontrar todas las ${rootNote} escondidas en el mástil.`}
        steps={[
          "Lee la explicación: la tónica es la nota principal de la escala",
          "Busca en el mástil las notas que se llaman igual que la escala",
          "Haz clic en cada una que encuentres para descubrirla",
          "Toca la nota en tu instrumento para confirmar que suena igual",
        ]}
      />

      <PracticeIntroModal
        isOpen={showTriadOrderedIntro}
        onStart={() => {
          setShowTriadOrderedIntro(false);
            setTriadOrderedMode(true);
          setOrderedSelection([]);
          setOrderedTriadsDone(new Set());
          setTriadGroups([]);
          setCurrentTriadSelection([]);
          setIsPlaying(false);
        }}
        onClose={() => { setShowTriadOrderedIntro(false); }}
        variant="learn"
        buttonText="Comenzar misión"
        title={`El acorde de ${rootNote}`}
        missionText={`Un acorde está formado por 3 notas: la Fundamental (I), la 3ra (III) y la 5ta (V). En ${rootNote} Mayor son: ${rootNote}, ${scalePattern.notes[2] || "3ª"} y ${scalePattern.notes[4] || "5ª"}. Tu misión: formar ${orderedTriadTargetCount} acordes en orden.`}
        steps={[
          `Paso 1: selecciona la Fundamental (I) — la nota ${rootNote}`,
          `Paso 2: selecciona la 3ra (III) — la nota ${scalePattern.notes[2] || "3ª"}`,
          `Paso 3: selecciona la 5ta (V) — la nota ${scalePattern.notes[4] || "5ª"}`,
          "Repetí hasta completar 5 triadas en diferentes posiciones",
        ]}
      />

      <PracticeIntroModal
        isOpen={showTriadFreeIntro}
        onStart={() => {
          setShowTriadFreeIntro(false);
          setTriadFreeMissionMode(true);
          setFreeTriadsDone(new Set());
          setTriadGroups([]);
          setCurrentTriadSelection([]);
          setIsPlaying(true);
        }}
        onClose={() => { setShowTriadFreeIntro(false); }}
        variant="learn"
        buttonText="Comenzar misión libre"
        title={`Triadas libres de ${rootNote}`}
        missionText={`Ahora que sabés cuáles son las 3 notas del acorde de ${rootNote} Mayor, tu misión es agruparlas de cualquier forma. El orden ya no importa: lo importante es reconocer las 3 notas del acorde.`}
        steps={[
          "Haz clic en cualquier nota del acorde (I, III o V)",
          "Seleccioná la segunda nota que falta",
          "Completá con la tercera nota",
          "Repetí hasta formar 5 triadas desde cualquier posición",
        ]}
      />

      <PracticeIntroModal
        isOpen={showPentatonicIntro}
        onStart={async () => {
          setShowPentatonicIntro(false);
          setPentatonicMissionMode(true);
          setPentatonicSubStep(0);
          setPentatonicPathSelection([]);
          setPentatonicPathsDone(new Set());
          setPentatonicPlayTarget([]);
          setPentatonicPlayIndex(0);
          setIsPlaying(true);
          await startListening();
        }}
        onClose={() => { setShowPentatonicIntro(false); }}
        variant="learn"
        buttonText="Comenzar misión pentatónica"
        title={`Escala pentatónica de ${rootNote}`}
        missionText={`La escala pentatónica es una de las más usadas en el mundo. Tiene 5 notas: ${rootNote} (I), ${scalePattern.notes[1] || "2ª"} (II), ${scalePattern.notes[2] || "3ª"} (III), ${scalePattern.notes[4] || "5ª"} (V) y ${scalePattern.notes[5] || "6ª"} (VI). Tu misión: construir el camino desde 3 tónicas diferentes y luego tocarlas con tu guitarra.`}
        steps={[
          "Misión 1–3: construí el camino pentatónico desde cada tónica en orden ascendente",
          "Empezá siempre por la tónica indicada",
          "Seguí con 2ª, 3ª, 5ª y 6ª en orden ascendente y en cuerdas distintas",
          "Misión 4: tocá las notas en tu guitarra y el micrófono las validará",
        ]}
      />

      <PracticeIntroModal
        isOpen={showPentatonicPathComplete}
        onStart={() => {
          setShowPentatonicPathComplete(false);
          if (pentatonicPathCompleteProps.isFinal) {
            // Pasar a misión de micrófono
            setPentatonicSubStep(3);
            setPentatonicPathSelection([]);
            setIsPlaying(true);
            // Usar el ref para asegurar que tenemos todos los caminos
            const playTarget = pentatonicSavedPathsRef.current.flat();
            setPentatonicPlayTarget(playTarget);
            setPentatonicPlayIndex(0);
          } else {
            // Avanzar a siguiente sub-misión
            setPentatonicSubStep((s) => (s + 1) as 0 | 1 | 2 | 3);
            setPentatonicPathSelection([]);
          }
        }}
        onClose={() => { setShowPentatonicPathComplete(false); }}
        variant="completed"
        buttonText={pentatonicPathCompleteProps.nextLabel}
        secondaryButton={{
          text: "Continuar más tarde",
          onClick: () => {
            setShowPentatonicPathComplete(false);
            setPentatonicMissionMode(false);
            setCurrentMissionStep(0);
          },
        }}
        title={pentatonicPathCompleteProps.title}
        missionText={pentatonicPathCompleteProps.message}
      />

      <PracticeIntroModal
        isOpen={showOctaveIntro}
        onStart={async () => {
          setShowOctaveIntro(false);
          setOctaveMissionMode(true);
          setOctaveSubStep(0);
          setOctavePathSelection([]);
          setOctavePathsDone(new Set());
          setOctavePlayTarget([]);
          setOctavePlayIndex(0);
          setIsPlaying(true);
          await startListening();
        }}
        onClose={() => { setShowOctaveIntro(false); }}
        variant="learn"
        buttonText="Comenzar misión de escala"
        title={`Escala de ${rootNote}`}
        missionText={`La escala completa tiene 8 notas: ${rootNote} (I), ${scalePattern.notes[1] || "2ª"} (II), ${scalePattern.notes[2] || "3ª"} (III), ${scalePattern.notes[3] || "4ª"} (IV), ${scalePattern.notes[4] || "5ª"} (V), ${scalePattern.notes[5] || "6ª"} (VI), ${scalePattern.notes[6] || "7ª"} (VII) y ${rootNote} (VIII). Tu misión: construir el camino desde 3 tónicas diferentes y luego tocarlas con tu guitarra.`}
        steps={[
          "Misión 1–3: construí el camino de la escala desde cada tónica en orden ascendente",
          "Empezá siempre por la tónica indicada",
          "Seguí con 2ª, 3ª, 4ª, 5ª, 6ª, 7ª y octava en orden ascendente",
          "Misión 4: tocá las notas en tu guitarra y el micrófono las validará",
        ]}
      />

      <PracticeIntroModal
        isOpen={showOctavePathComplete}
        onStart={() => {
          setShowOctavePathComplete(false);
          if (octavePathCompleteProps.isFinal) {
            // Pasar a misión de micrófono
            setOctaveSubStep(3);
            setOctavePathSelection([]);
            setIsPlaying(true);
            // Usar el ref para asegurar que tenemos todos los caminos
            const playTarget = octaveSavedPathsRef.current.flat();
            setOctavePlayTarget(playTarget);
            setOctavePlayIndex(0);
          } else {
            // Avanzar a siguiente sub-misión
            setOctaveSubStep((s) => (s + 1) as 0 | 1 | 2 | 3);
            setOctavePathSelection([]);
          }
        }}
        onClose={() => { setShowOctavePathComplete(false); }}
        variant="completed"
        buttonText={octavePathCompleteProps.nextLabel}
        secondaryButton={{
          text: "Continuar más tarde",
          onClick: () => {
            setShowOctavePathComplete(false);
            setOctaveMissionMode(false);
            setCurrentMissionStep(0);
          },
        }}
        title={octavePathCompleteProps.title}
        missionText={octavePathCompleteProps.message}
      />

      <PracticeIntroModal
        isOpen={showMissionComplete}
        onStart={() => {
          setShowMissionComplete(false);
          if (surface === "keyboard" && pedagogicalLevel === 1) {
            const completedStep = focusLevel as PianoFocusStep;
            if (completedStep < 5) {
              pianoKeepIntroRef.current = true;
              const next = (completedStep + 1) as 1 | 2 | 3 | 4 | 5;
              setFocusLevel(next);
              setShowPianoLearnIntro(true);
              return;
            }
          }
          if (missionCompleteProps.nextStep === 1) {
            setFocusLevel(2);
            setCurrentMissionStep(1);
            setShowTriadOrderedIntro(true);
          } else if (missionCompleteProps.nextStep === 2) {
            setFocusLevel(2);
            setCurrentMissionStep(2);
            setShowTriadFreeIntro(true);
          } else if (missionCompleteProps.nextStep === 3) {
            setFocusLevel(3);
            setCurrentMissionStep(3);
            setShowPentatonicIntro(true);
          } else {
            setCurrentMissionStep(0);
          }
        }}
        onClose={() => { setShowMissionComplete(false); }}
        variant="completed"
        buttonText={missionCompleteProps.nextLabel}
        secondaryButton={{
          text: "Continuar más tarde",
          onClick: () => {
            setShowMissionComplete(false);
            setCurrentMissionStep(0);
            setTonicDiscoveryMode(false);
            setTriadOrderedMode(false);
            setTriadFreeMissionMode(false);
            setPentatonicMissionMode(false);
            setOctaveMissionMode(false);
          },
        }}
        title={missionCompleteProps.title}
        missionText={missionCompleteProps.message}
        steps={missionCompleteProps.steps}
        footerTip={missionCompleteProps.footerTip}
      />
    </WorkbenchLayout>
    </SongLearnCanvasBackdrop>
  );
};

export default ScaleExplorerPage;
